import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Alert, Button, Cascader, DatePicker, Form, Input, message, Modal, Popconfirm, Radio, Select, Typography, Upload, Row, Col, Space, Tag, Switch, Spin, Avatar, Tooltip } from 'antd';
import { ArrowDownCircle, ArrowUpCircle, CheckCircleFill, PlusCircle, XCircle, XCircleFill } from 'react-bootstrap-icons';
import dayjs from 'dayjs';
import ImgCrop from 'antd-img-crop';
import RichEditor from '@components/common/RichEditor';
import { saveExpertApi, showExpertApi, editInfoApi, postExpertApi, delPoistionApi, getExpertListApi, sysdictionaryApi, delPeopleApi, changeExpertMobile, checkBaiduBaikeEntryApi, getOrganizations } from '@services/Experts';
import { authdictionaryApi } from '@services/Conference/sign';
import { organizationsSaveAndUpdateApi, organizationsListApi } from '@services/DC/Organization';
import config from '@config';
import { regionOptions } from '@utils/region';
import national from '@utils/national';
import { expertLevels, cardTypes, honoraryList, orgTypeList, identityTypes } from '@utils/constants';
import { pageFormItemLayout } from '@utils/property';
import './index.less';
import { scrollToFirstFormItemHasError, prepareMedLevelGrade, parseExpertUpdateTraces, buildExpertUpdateTracesJsonForPhoneChange, getExpertUpdateTrace, mergeExpertUpdateTraceEntriesForEdit, normalizeExpertIntroHtmlForCompare, buildAuthDictMaps, formatOfficeAndPost } from '@utils/utils';
import { logExpertCreate, logExpertUpdate, logExpertChangeMobile } from '@/utils/expertLogger';
import { canViewExpertMobileFull } from '@/utils/expertMobileVisibility';
import OperationLogDrawer from '@components/common/OperationLogDrawer';
import { debounce, isEmpty, cloneDeep } from 'lodash';
import { prepareBCOSSUrl } from '../../../utils/utils';

const extractFileNameFromUrl = (url = '') => {
    if (!url) return '';

    try {
        const decodedUrl = decodeURIComponent(url);
        const withoutQuery = decodedUrl.split('?')[0];
        return withoutQuery.split('/').pop() || '';
    } catch (error) {
        const withoutQuery = url.split('?')[0];
        return withoutQuery.split('/').pop() || '';
    }
};

const sanitizeFilenamePart = (value = '') => value.replace(/[\\/:*?"<>|]/g, '_');

/** 是否为中国居民身份证类型（与 cardTypes 中 code=1 一致；接口/表单可能为数字 1 或字符串 "1"） */
const isCnIdCardType = cardType => Number(cardType) === 1;

/** 证件号规范化：身份证仅保留数字与末位 X（x 转大写）；其他类型去掉首尾与中间空格，便于通过校验与落库 */
const normalizeIdCardInput = (raw, cardType) => {
    if (raw == null || raw === '') {
        return raw;
    }
    const s = String(raw);
    if (isCnIdCardType(cardType)) {
        let v = s.replace(/[^\dXx]/g, '');
        if (v.length > 18) {
            v = v.slice(0, 18);
        }
        if (v.length === 18) {
            v = `${v.slice(0, 17)}${v[17] === 'x' || v[17] === 'X' ? 'X' : v[17]}`;
        }
        return v;
    }
    if (cardType != null && cardType !== '') {
        return s.trim().replace(/\s+/g, '');
    }
    return s.trim();
};

/** 居民身份证 18 位校验位（GB 11643），与末位一致则返回 true */
const CN_ID_CARD_WEIGHTS = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
const CN_ID_CARD_CHECK_CHARS = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];

const isValidCnIdCard18Checksum = value => {
    if (value == null || !/^\d{17}[\dX]$/i.test(String(value))) {
        return false;
    }
    const s = String(value).toUpperCase();
    let sum = 0;
    for (let i = 0; i < 17; i++) {
        sum += parseInt(s.charAt(i), 10) * CN_ID_CARD_WEIGHTS[i];
    }
    const expected = CN_ID_CARD_CHECK_CHARS[sum % 11];
    const actual = s.charAt(17);
    return expected === actual;
};

/** 银行账号：仅保留数字 */
const digitsOnlyBankAccount = raw => String(raw ?? '').replace(/\D/g, '');

/** 与编辑提交时任职数据处理一致，用于 update_traces 变更对比（勿与表单共用引用，内部会 clone） */
const formatDateByPickerTypeForSaveStatic = (date, pickerType) => {
    let result = '';
    if (date && dayjs(date).isValid()) {
        switch (pickerType) {
            case 'date':
                result = dayjs(date).format('YYYY-MM-DD');
                break;
            case 'month':
                result = dayjs(date).format('YYYY-MM-01');
                break;
            case 'year':
                result = dayjs(date).format('YYYY-01-01');
                break;
            default:
                result = dayjs(date).format('YYYY-MM-DD');
                break;
        }
    }
    return result;
};

const prepareExperiencesForExpertEditSubmit = experiences => {
    if (!Array.isArray(experiences)) {
        return [];
    }
    let newArr = cloneDeep(experiences);
    newArr = newArr.filter(v => {
        if (!v?.unitName) {
            delete v?.unitName;
        } else if (v.childs) {
            v.childs = v?.childs?.filter(it => {
                if (it) {
                    const o = Object.values(it).filter(i => i);
                    if (o.length) {
                        return it !== undefined;
                    }
                }
                return false;
            });
        }

        if (JSON.stringify(v) !== '{}') {
            return true;
        }
        return false;
    });
    newArr = newArr.filter(it => it !== undefined);

    return newArr.map(exp => {
        const expCopy = { ...exp };
        if (Array.isArray(expCopy.childs)) {
            expCopy.childs = expCopy.childs.map(child => {
                const childCopy = { ...child };
                const pickerType = childCopy.tenureDateType;
                const toStr = d => (d && dayjs(d).isValid() ? formatDateByPickerTypeForSaveStatic(d, pickerType) : null);
                childCopy.tenureStartTime = toStr(childCopy.tenureStartTime);
                childCopy.tenureEndTime = toStr(childCopy.tenureEndTime);
                return childCopy;
            });
        }
        return expCopy;
    });
};

/** 收款信息字段序列化，用于 update_traces 变更对比 */
const serializeLaborfeeFieldsForCompare = values => {
    if (!values) {
        return '';
    }
    const birthday = values.birthday && dayjs(values.birthday).isValid() ? dayjs(values.birthday).format('YYYY-MM-DD') : '';
    const idcardTypeNorm =
        values.idcardType == null || values.idcardType === ''
            ? values.idcardType
            : (() => {
                  const n = Number(values.idcardType);
                  return Number.isNaN(n) ? values.idcardType : n;
              })();
    return JSON.stringify({
        idcardType: idcardTypeNorm,
        idCard: values.idCard ?? '',
        birthday,
        nationality: values.nationality ?? '',
        birthPlace: values.birthPlace ?? '',
        bankType: values.bankType ?? 1,
        payeeUsername: values.payeeUsername ?? '',
        payeePhone: values.payeePhone ?? '',
        payeeAddress: values.payeeAddress ?? '',
        bankName: values.bankName ?? '',
        bankCardNo: values.bankCardNo ?? '',
        swiftBicCode: values.swiftBicCode ?? '',
    });
};

const { Text } = Typography;
const { TextArea } = Input;

/**
 * 新增/编辑专家
 *
 * 1. 手机号为 1 开头的 11 位数字，100 开头时为无手机号。
 * 1.1 如 hasMobile 为 false 时无手机号，请求新增专家 API 时不传 memMobile 字段，API 接收到数据后自动为无手机号专家生成一个 100 开头的手机号。
 * 2. 编辑专家时，如 GET 请求返回的专家 memMobile 为 100 开头，则 hasMobile 为 false，且 memMobile 字段隐藏，不可编辑。
 * 2.1 编辑无手机号专家时，请求更新专家信息 API 时同样不传 memMobile 字段，API 接收到数据后不更新手机号（保持 100 开头手机号不变），但会更新其他字段。
 * 3. 新增专家时，即使无手机号查询权限，也展示手机号 Input 控件。
 * 3.1 编辑专家时，100 开头的手机号视为无手机号，无手机号时所有人均可在编辑专家时填写手机号。
 * 3.2 编辑专家时，无手机号时，hasMobile 为 false，且 memMobile 字段隐藏。
 *
 */

const Expert = forwardRef((props, ref) => {
    const { embedded = false, expertId: embeddedExpertId, onSaveSuccess, onDirtyChange, onStatusChange, onViewExpertDetail } = props;
    const isEmbedded = embedded;
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    console.log('专家详情页 state:', location.state);

    // embedded 模式下 expertId 可能为 0（新增），不能用 embeddedExpertId || params.id，否则 0 会落到路由 params.id（如会议 ID）上误当专家 ID 请求详情
    let expertId;
    if (isEmbedded) {
        const raw = embeddedExpertId !== undefined && embeddedExpertId !== null ? embeddedExpertId : 0;
        expertId = parseInt(raw, 10);
        expertId = Number.isFinite(expertId) ? expertId : 0;
    } else {
        expertId = parseInt(embeddedExpertId || params.id || 0, 10) || 0;
    }

    const [expertInfo, setExpertInfo] = useState({});
    const [nationalityDicList, setNationalityDicList] = useState([]);

    const [expertForm] = Form.useForm();
    const idcardType = Form.useWatch('idcardType', expertForm);

    const option = { headers: { 'Content-Type': 'multipart/form-data' } };

    const [changeExpertMobileForm] = Form.useForm();
    const [visibleChangeExpertMobile, setVisibleChangeExpertMobile] = useState(false);
    const [logDrawerOpen, setLogDrawerOpen] = useState(false);

    const [baiduBaikeUrl, setBaiduBaikeUrl] = useState('');

    const beforeDataRefs = useRef(null); // 编辑前的表单数据快照
    const isProgrammaticFormUpdateRef = useRef(false);
    const programmaticFormUpdateTimerRef = useRef(null);
    const currentExpertLoadVersionRef = useRef(0);
    const currentExpertIdRef = useRef(expertId);
    const userDirtyRef = useRef(false);

    useEffect(() => {
        currentExpertIdRef.current = expertId;
    }, [expertId]);

    //图片列表
    const [fileList, setFileList] = useState([]);
    const [publicityPhotoList, setPublicityPhotoList] = useState([]); // 宣传照片
    const [introFileInfo, setIntroFileInfo] = useState(null);
    const [introFileDownloading, setIntroFileDownloading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [publicPreviewOpen, setPublicPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [size, setSize] = useState(false);
    const [big, setBig] = useState(false);
    const [mode, setMode] = useState(false);
    const laborRef = useRef(null);
    const moreRef = useRef(null);
    const accountRef = useRef(null);
    const [prodDetails, setProdDetails] = useState('');

    /** 编辑页：个人介绍 / 单位任职 / 收款信息 变更检测基线（与 show 加载后表单一致） */
    const traceBaselineRef = useRef(null);

    const snapshotTraceBaseline = () => {
        const v = expertForm.getFieldsValue();
        traceBaselineRef.current = {
            introNorm: normalizeExpertIntroHtmlForCompare(v.memIntroduction != null && v.memIntroduction !== '' ? v.memIntroduction : prodDetails),
            introFile: v.introFile || '',
            experiencesKey: JSON.stringify(prepareExperiencesForExpertEditSubmit(v.experiences)),
            laborfeeKey: serializeLaborfeeFieldsForCompare(v),
        };
    };

    // 单位任职
    const [unitForm] = Form.useForm();
    // antd v6 Form.List 不再将未注册子项数据包含在 getFieldsValue 中，
    // 需要使用 useWatch + preserve: true 来获取完整的嵌套数据
    const watchedExperiences = Form.useWatch('experiences', { form: expertForm, preserve: true }) || [];
    const [editIndex, setEditIndex] = useState(0);
    const [unitInfo, setUnitInfo] = useState({});
    const [authType, setAuthType] = useState('');
    const [authDict, setAuthDict] = useState([]);
    const [unitSearchKeyword, setUnitSearchKeyword] = useState('');
    const [units, setUnits] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [createUnitForm] = Form.useForm();
    const [visibleCreateUnitModal, setVisibleCreateUnitModal] = useState(false);
    const [viewExpertVisible, setViewExpertVisible] = useState(false); // 预览对外展示信息
    const datePickerTypes = [
        { label: '按年份选择', value: 'year' },
        { label: '按月份选择', value: 'month' },
        { label: '任期未知', value: '' },
    ];
    const messageText = '未分类机构，请在数据中心 > 机构管理 > 更新机构类型后可选择';

    // 原手机号
    const [formalMobile, setFormalMobile] = useState('');
    const [otherMobile, setOtherMobile] = useState('');

    const [canViewMobile, setCanViewMobile] = useState(false); // 是否可查看手机号

    const [someNameList, setSomeNameList] = useState([]);

    const [loading, setLoading] = useState(false);
    const [bankAccountType, setBankAccountType] = useState(1); // 1: 境内银行，2: 境外银行

    const introUpdateTrace = useMemo(() => getExpertUpdateTrace(expertInfo, 'intro'), [expertInfo]);
    const unitUpdateTrace = useMemo(() => getExpertUpdateTrace(expertInfo, 'unit'), [expertInfo]);
    const laborfeeUpdateTrace = useMemo(() => getExpertUpdateTrace(expertInfo, 'laborfee'), [expertInfo]);

    // 检查百度百科词条是否存在
    const checkBaiduBaike = async name => {
        if (!name) return;

        try {
            const res = await checkBaiduBaikeEntryApi({ keyword: name });
            if (res.status_code === 200 && res.data) {
                const encodedName = encodeURIComponent(name);
                setBaiduBaikeUrl(`https://baike.baidu.com/item/${encodedName}`);
            } else {
                setBaiduBaikeUrl('');
            }
        } catch (error) {
            console.error('检查百度百科词条失败:', error);
            setBaiduBaikeUrl('');
        }
    };

    // 从身份证号提取出生日期
    const extractBirthdayFromIdCard = (idCardValue, cardType) => {
        // 当证件类型为1（身份证）且证件号格式正确时，自动提取出生日期
        if (isCnIdCardType(cardType) && /^\d{17}[\dX]$/.test(idCardValue)) {
            // 身份证号第7-14位是出生日期，格式为YYYYMMDD
            const birthDateStr = idCardValue.substring(6, 14);
            const year = birthDateStr.substring(0, 4);
            const month = birthDateStr.substring(4, 6);
            const day = birthDateStr.substring(6, 8);
            // 验证日期是否有效
            const birthDate = dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD');
            if (birthDate.isValid()) {
                return birthDate;
            }
        }
        return null;
    };

    useEffect(() => {
        if (!!expertInfo?.memUsername) {
            checkBaiduBaike(expertInfo.memUsername);
        }
    }, [expertInfo]);

    const applyExpertFormValues = useCallback(
        values => {
            if (programmaticFormUpdateTimerRef.current) {
                clearTimeout(programmaticFormUpdateTimerRef.current);
            }

            isProgrammaticFormUpdateRef.current = true;
            expertForm.setFieldsValue(values);
            programmaticFormUpdateTimerRef.current = setTimeout(() => {
                isProgrammaticFormUpdateRef.current = false;
                programmaticFormUpdateTimerRef.current = null;
            }, 0);
        },
        [expertForm],
    );

    // 获取单位信息
    const getUnitsByIds = useCallback(
        async (ids, newExperiences, loadVersion, targetExpertId) => {
            const res = await organizationsListApi({
                page: 1,
                size: 100,
                keywordList: ids,
            });

            if (currentExpertLoadVersionRef.current !== loadVersion || currentExpertIdRef.current !== targetExpertId || userDirtyRef.current) {
                return;
            }

            // console.log(res);

            if (!!res.data) {
                const datas = res.data.records || [];

                newExperiences?.forEach(item => {
                    datas?.forEach(unit => {
                        if (unit.id === item.memUnitId) {
                            item.unitName = unit.name;
                            item.orgType = unit.orgType;
                            item.authType = identityTypes.find(item => item.orgType === unit.orgType)?.code;

                            item.childs.forEach(child => {
                                child.tenureStartTime = child.tenureStartTime ? child.tenureStartTime : undefined;
                                child.tenureEndTime = child.tenureEndTime ? child.tenureEndTime : undefined;
                                child.orgType = unit.orgType;
                                child.authType = identityTypes.find(item => item.orgType === unit.orgType)?.code;
                            });
                        }
                    });
                });

                console.log('newExperiences', newExperiences);

                applyExpertFormValues({ experiences: newExperiences });
                setExpertInfo(expertInfo => ({ ...expertInfo, experiences: newExperiences }));
                onDirtyChange?.(false);
            }
        },
        [applyExpertFormValues, onDirtyChange],
    );

    // 获取专家信息（单次 show 接口，整合原 base-info / extend-info / account-info）
    useEffect(() => {
        if (expertId) {
            const loadVersion = currentExpertLoadVersionRef.current + 1;
            currentExpertLoadVersionRef.current = loadVersion;
            userDirtyRef.current = false;
            setLoading(true);

            showExpertApi({ id: expertId })
                .then(async res => {
                    if (currentExpertLoadVersionRef.current !== loadVersion || currentExpertIdRef.current !== expertId) {
                        return;
                    }

                    if (res.status_code !== 200) {
                        return;
                    }

                    let data = { ...res.data };

                    if (data.idcardType != null && data.idcardType !== '') {
                        const idt = Number(data.idcardType);
                        if (!Number.isNaN(idt)) {
                            data.idcardType = idt;
                        }
                    }

                    if (data.idCard && data.idcardType) {
                        data.idCard = normalizeIdCardInput(data.idCard, data.idcardType);
                    }

                    data.birthday = !!data.birthday && dayjs(data.birthday, 'YYYY-MM-DD').isValid() ? dayjs(data.birthday, 'YYYY-MM-DD') : undefined;

                    // 荣誉头衔处理（兼容数组/JSON 字符串/普通字符串/对象）
                    if (!data.honoraryTitles) {
                        data.honoraryTitles = [];
                    } else {
                        try {
                            if (Array.isArray(data.honoraryTitles)) {
                                // 已是数组，直接使用
                                data.honoraryTitles = data.honoraryTitles.filter(Boolean);
                            } else if (typeof data.honoraryTitles === 'string') {
                                // 先尝试按 JSON 数组解析
                                try {
                                    const parsed = JSON.parse(data.honoraryTitles);
                                    if (Array.isArray(parsed)) {
                                        data.honoraryTitles = parsed.filter(Boolean);
                                    } else if (typeof parsed === 'string') {
                                        data.honoraryTitles = [parsed].filter(Boolean);
                                    } else if (parsed && typeof parsed === 'object') {
                                        data.honoraryTitles = Object.values(parsed).filter(Boolean);
                                    } else {
                                        data.honoraryTitles = [];
                                    }
                                } catch (_jsonErr) {
                                    // 非 JSON，按常见分隔符拆分
                                    data.honoraryTitles = data.honoraryTitles.split(/[、,，\s]+/).filter(Boolean);
                                }
                            } else if (typeof data.honoraryTitles === 'object') {
                                // 对象场景，取值数组
                                data.honoraryTitles = Object.values(data.honoraryTitles || {}).filter(Boolean);
                            } else {
                                data.honoraryTitles = [];
                            }
                        } catch (e) {
                            console.error('荣誉头衔解析失败:', e);
                            data.honoraryTitles = [];
                        }
                    }

                    const fileUrl = data.expertAvatar ? [{ url: data.expertAvatar }] : [];
                    setFileList(fileUrl);

                    const publicityPhoto = data.publicityPhoto ? [{ url: data.publicityPhoto }] : [];
                    setPublicityPhotoList(publicityPhoto);

                    const introFileUrl = data.introFile ? data.introFile : '';
                    data.introFile = introFileUrl;
                    if (introFileUrl) {
                        setIntroFileInfo({
                            url: introFileUrl,
                            name: data.introFileName || extractFileNameFromUrl(introFileUrl),
                        });
                    } else {
                        setIntroFileInfo(null);
                    }

                    // updateTraces 仅保留在 expertInfo，不写入 Form，避免误提交到编辑接口（接口可能返回 updateTraces 或 update_traces）
                    const updateTracesParsed = parseExpertUpdateTraces(data.updateTraces ?? data.update_traces);
                    data.updateTraces = updateTracesParsed;

                    // 编辑专家时，有非 100 开头手机号时，hasMobile 为 true，否则为 false
                    data.hasMobile = data.memMobile && !data.memMobile.startsWith('100') && /^1\d{10}$/.test(data.memMobile);

                    // 如果是无手机号专家，清空 memMobile 字段
                    if (!data.hasMobile) {
                        data.memMobile = '';
                    }

                    // 请求专家信息后，100 开头时，设置为 ''
                    if (data.memMobile && data.memMobile.startsWith('100')) {
                        setFormalMobile('');
                    } else if (data.memMobile && /^1\d{10}$/.test(data.memMobile)) {
                        setFormalMobile(data.memMobile);
                    } else {
                        setFormalMobile('');
                    }

                    // 银行账户类型（原 account-info）
                    const accountType = data.bankType || 1;
                    setBankAccountType(accountType);

                    // 省市区级联
                    let area;
                    if (data.provinceCode === null || data.provinceCode === '') {
                        area = [];
                    } else if (data?.cityCode === null || data?.cityCode === '') {
                        area = [data.provinceCode];
                    } else if (data?.districtCode === null || data?.districtCode === '') {
                        area = [data.provinceCode, data.cityCode];
                    } else {
                        area = [data.provinceCode, data.cityCode, data.districtCode];
                    }
                    data = { ...data, area };

                    // 相关联系人：空或 [] 均视为无，默认不展开表单
                    // 过滤掉仅包含 id 等占位字段、无姓名/手机/邮箱的空联系人，避免 Form.List 自动渲染空行
                    if (Array.isArray(data?.contacts)) {
                        const validContacts = data.contacts.filter(v => v && ['name', 'phone', 'email'].some(key => v[key] !== undefined && v[key] !== null && v[key] !== ''));
                        data.contacts = validContacts.length ? validContacts : undefined;
                    } else {
                        data.contacts = undefined;
                    }

                    const dataForForm = { ...data };
                    delete dataForForm.updateTraces;

                    setProdDetails(data.memIntroduction || '');
                    applyExpertFormValues(dataForForm);

                    setExpertInfo(data);
                    onStatusChange?.(data.status);
                    userDirtyRef.current = false;
                    onDirtyChange?.(false);

                    setEditIndex(-1);

                    // 获取单位任职信息
                    const unitIds = [];
                    (data.experiences || []).forEach(item => {
                        if (!!item.memUnitId) {
                            unitIds.push(item.memUnitId);
                        }
                    });
                    if (!!unitIds.length) {
                        await getUnitsByIds(unitIds, data.experiences, loadVersion, expertId);
                    }

                    // 收集编辑前快照与 update_traces 变更检测基线（任职机构异步补全后再拍一次）
                    setTimeout(() => {
                        snapshotTraceBaseline();
                        beforeDataRefs.current = expertForm.getFieldsValue();
                    }, 0);
                })
                .finally(() => {
                    if (currentExpertLoadVersionRef.current === loadVersion && currentExpertIdRef.current === expertId) {
                        setLoading(false);
                    }
                });
        } else {
            // 新增专家时，初始化银行账户类型为 1（境内银行）
            setBankAccountType(1);
        }
    }, [applyExpertFormValues, expertId, getUnitsByIds, onDirtyChange, onStatusChange]);

    useEffect(() => {
        return () => {
            if (programmaticFormUpdateTimerRef.current) {
                clearTimeout(programmaticFormUpdateTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        // 与查看页、utils/expertMobileVisibility 共用：无正式手机号时均可操作；有正式手机号时仅特权管理员可见
        setCanViewMobile(canViewExpertMobileFull(formalMobile));
    }, [formalMobile]);

    const expertIntroImageUploadHandler = useCallback(async blobInfo => {
        const formData = new FormData();
        formData.append('file', blobInfo.blob());
        formData.append('path', `upload/`);
        const hide = message.loading('上传中...', 0);
        try {
            const res = await postExpertApi(formData);
            const url = `${config.OSS_PREFIX}/${res.data}`;
            hide();
            return url;
        } catch (e) {
            hide();
            throw e;
        }
    }, []);

    //头像裁剪
    const beforeCropCheck = (file, bool) => {
        if (file.type !== 'image/png' && file.type !== 'image/jpg' && file.type !== 'image/jpeg') {
            message.error('只支持JPG/JPEG/PNG格式');
            return null;
        }

        if (file.size > 10 * 1024 * 1024) {
            message.error('图片大小不能超过10MB');
            return null;
        }

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = event => {
                const res = event.target.result;
                const imgDom = document.createElement('img');
                imgDom.src = res;
                imgDom.onload = () => {
                    console.log(imgDom.width, imgDom.height);
                    if (imgDom.width < 300 || imgDom.height < 300) {
                        bool && message.warning('头像尺寸不能小于300x300px');
                        resolve(false);
                    } else {
                        resolve(true);
                    }
                };
            };
            reader.readAsDataURL(file);
        });
    };

    const viewDetail = row => {
        if (onViewExpertDetail) {
            onViewExpertDetail(row.id);
            return;
        }
        navigate(`/dc/experts?detail=${row.id}`);
    };

    // 搜索姓名
    const searchName = async e => {
        if (!e.target.value) {
            setSomeNameList([]);
            return;
        }
        const res = await getExpertListApi({
            memUsername: e.target.value,
            page: 1,
            pageSize: 20,
        });
        if (res && res.status_code === 200) {
            setSomeNameList(res.data.list);
        }
    };

    // 移除图片
    const onRemove = value => {
        const newFileList = fileList.filter(file => file.url !== value.url);
        setFileList(newFileList);
    };

    const handlePreview = file => {
        if (mode === true) {
            message.error('只能上传 jpg/png/jpeg 格式');
        } else if (size === true) {
            message.error('头像大小不超过5MB');
        } else {
            setPreviewImage(file.url || file.preview);
            setPreviewOpen(true);
        }
    };

    const beforeUpload = file => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
            message.error('只能上传 jpg/png/jpeg 格式');
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 10;
        if (!isLt5M) {
            message.error('头像大小不超过10MB');
            return false;
        }
        return isJpgOrPng && isLt5M;
        // return isJpgOrPng && isLt5M && fn(file, true);
    };

    const upload = async value => {
        const formData = new FormData();
        formData.append('file', value.file);
        formData.append('path', `avatar/`);
        const res = await postExpertApi(formData, option);
        console.log(res);

        if (res.status_code === 200) {
            setFileList([{ url: config.OSS_PREFIX + `/${res.data}` }]);
        }
    };

    // 移除图片
    const onPublicRemove = value => {
        const newFileList = publicityPhotoList.filter(file => file.url !== value.url);
        setPublicityPhotoList(newFileList);
    };

    const handlePublicPreview = file => {
        if (mode === true) {
            message.error('只能上传 jpg/png/jpeg 格式');
        } else if (size === true) {
            message.error('宣传照片大小不超过20MB');
        } else {
            setPreviewImage(file.url || file.preview);
            setPublicPreviewOpen(true);
        }
    };

    const publicBeforeUpload = file => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
            message.error('只能上传 jpg/png/jpeg 格式');
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 50;
        if (!isLt5M) {
            message.error('宣传照片大小不超过50MB');
            return false;
        }
        return isJpgOrPng && isLt5M;
        // return isJpgOrPng && isLt5M && fn(file, true);
    };

    const publicUpload = async value => {
        const formData = new FormData();
        formData.append('file', value.file);
        formData.append('path', `avatar/`);
        const res = await postExpertApi(formData, option);
        console.log(res);
        if (res.status_code === 200) {
            setPublicityPhotoList([{ url: config.OSS_PREFIX + `/${res.data}` }]);
        }
    };

    const introFileBeforeUpload = file => {
        if (!/\.(docx?|pptx?|pdf|jpe?g|png|rar|zip)$/i.test(file.name || '')) {
            message.error('仅支持 DOC/DOCX、PPT/PPTX、PDF、JPG/PNG、ZIP/RAR 格式');
            return Upload.LIST_IGNORE;
        }

        const isWithinSize = file.size / 1024 / 1024 <= 50;
        if (!isWithinSize) {
            message.error('附件大小不超过50MB');
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const handleIntroFileUpload = async value => {
        const formData = new FormData();
        formData.append('file', value.file);
        formData.append('path', `intro/`);

        try {
            const res = await postExpertApi(formData, option);
            if (res.status_code === 200) {
                const url = `${config.OSS_PREFIX}/${res.data}`;
                const name = value.file?.name || extractFileNameFromUrl(url);

                setIntroFileInfo({ url, name });
                expertForm.setFieldsValue({ introFile: url });
                message.success('附件上传成功');
                value.onSuccess?.(res, value.file);
            } else {
                message.error(res.message || '附件上传失败');
                value.onError?.(new Error(res.message || '附件上传失败'));
            }
        } catch (error) {
            console.error('简介附件上传失败:', error);
            message.error('附件上传失败');
            value.onError?.(error);
        }
    };

    const previewCancel = () => {
        setPreviewOpen(false);
    };

    const publicPreviewCancel = () => {
        setPublicPreviewOpen(false);
    };

    const onProdDetailsChange = v => {
        setProdDetails(v);
        expertForm.setFieldValue('memIntroduction', v);
    };

    const getIntroFileDownloadName = useCallback(() => {
        if (!introFileInfo?.url) return undefined;

        const expertNameRaw = expertForm.getFieldValue('memUsername') || '专家';
        const expertName = sanitizeFilenamePart(expertNameRaw.trim() || '专家');
        const fileName = introFileInfo.name || extractFileNameFromUrl(introFileInfo.url) || '';
        const ext = fileName.includes('.') ? fileName.split('.').pop() : '';

        return `${expertName}_简介${ext ? `.${ext}` : ''}`;
    }, [expertForm, introFileInfo]);

    const handleIntroFileDownload = useCallback(async () => {
        if (!introFileInfo?.url) return;

        setIntroFileDownloading(true);

        try {
            const response = await fetch(prepareBCOSSUrl(introFileInfo.url), { mode: 'cors', credentials: 'omit' });
            if (!response.ok) {
                throw new Error(`下载失败: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = getIntroFileDownloadName() || '简介附件';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('下载简介附件失败:', error);
            message.error('附件下载失败，请稍后重试');
        } finally {
            setIntroFileDownloading(false);
        }
    }, [getIntroFileDownloadName, introFileInfo]);

    const openChangeExpertMobileModal = () => {
        setVisibleChangeExpertMobile(true);
    };

    const closeChangeExpertMobileModal = () => {
        changeExpertMobileForm.resetFields();
        setVisibleChangeExpertMobile(false);
    };

    const onChangeExpertMobile = async () => {
        try {
            const values = await changeExpertMobileForm.validateFields();
            const { newMobile: memMobile } = values;

            if (memMobile !== formalMobile) {
                try {
                    const updateTraces = buildExpertUpdateTracesJsonForPhoneChange(expertInfo?.updateTraces);
                    const res = await changeExpertMobile({ id: expertId, memMobile, updateTraces });
                    if (res.status_code === 200) {
                        await logExpertChangeMobile(expertId, formalMobile, memMobile, true);
                        message.success('手机号更换成功');
                        setFormalMobile(memMobile);
                        setExpertInfo(prev => ({
                            ...prev,
                            memMobile,
                            updateTraces: parseExpertUpdateTraces(updateTraces),
                        }));
                        expertForm.setFieldsValue({
                            memMobile,
                            hasMobile: true, // 更换手机号后，设置为有手机号
                        });
                        setVisibleChangeExpertMobile(false);
                    } else {
                        await logExpertChangeMobile(expertId, formalMobile, memMobile, false, res.message);
                    }
                } catch (err) {
                    await logExpertChangeMobile(expertId, formalMobile, memMobile, false, err.message);
                }
            } else {
                message.warning('新手机号不能与原手机号一致，无需更换！');
            }
        } catch (e) {
            console.log(e);
        }
    };

    // 日期格式处理
    const formatDateByPickerTypeForSave = (date, pickerType) => {
        let result = '';
        if (date && dayjs(date).isValid()) {
            switch (pickerType) {
                case 'date':
                    result = dayjs(date).format('YYYY-MM-DD');
                    break;
                case 'month':
                    result = dayjs(date).format('YYYY-MM-01');
                    break;
                case 'year':
                    result = dayjs(date).format('YYYY-01-01');
                    break;
                default:
                    result = dayjs(date).format('YYYY-MM-DD');
                    break;
            }
        }

        return result;
    };

    // 日期显示格式（兼容 dayjs 或 字符串）
    const formatDateForDisplay = (date, pickerType) => {
        if (!date) return '';
        const m = dayjs(date);
        if (!m.isValid()) return '';
        if (pickerType === 'year') return m.format('YYYY年');
        if (pickerType === 'month') return m.format('YYYY年M月');
        return m.format('YYYY年M月D日');
    };

    // 新增单位任职
    const addUnitEmployment = () => {
        const employments = expertForm.getFieldValue('experiences') || [];
        expertForm.setFieldsValue({
            experiences: [
                ...employments,
                {
                    unitName: '',
                    memUnitId: undefined,
                },
            ],
        });
        setEditIndex(employments.length);
        setUnitInfo({
            unitName: '',
            memUnitId: undefined,
            isNewUnit: true,
        });
        unitForm.resetFields();
    };

    // 编辑单位任职
    const editUnitEmployment = index => {
        if (editIndex !== -1) {
            message.warning('请先保存当前单位任职！');
            return;
        } else {
            let employment = (expertForm.getFieldValue('experiences') || [])[index];
            employment = cloneDeep(employment);

            employment.childs.map(child => {
                child.tenureStartTime = child.tenureStartTime ? dayjs(child.tenureStartTime) : undefined;
                child.tenureEndTime = child.tenureEndTime ? dayjs(child.tenureEndTime) : undefined;
                // child.unitPost = child.unitPost ? child.unitPost : undefined;
                // child.unitOffice = child.unitOffice ? child.unitOffice : undefined;
                child.memPostCode = child.memPostCode && child.memPostCode.substring(0, 3) === employment.authType ? child.memPostCode : undefined;
                child.unitPost = child.memPostCode && child.memPostCode.substring(0, 3) === employment.authType ? child.unitPost : '';
                child.memOfficeCode = child.memOfficeCode && child.memOfficeCode.substring(0, 3) === employment.authType ? child.memOfficeCode : undefined;
                child.unitOffice = employment.authType === 'A01' ? (child.memOfficeCode && child.memOfficeCode.substring(0, 3) === employment.authType ? child.unitOffice : '') : child.unitOffice;
                return child;
            });

            setUnitInfo({ ...employment, isNewUnit: false });
            setAuthType(employment.authType);
            setUnits([
                {
                    id: employment.memUnitId,
                    name: employment.unitName,
                    orgType: employment.orgType,
                },
            ]);
            setEditIndex(index);
            unitForm.setFieldsValue(employment);

            console.log(employment, 'employment');
        }
    };

    // 获取字典名称
    const getDicName = (datas, dicCode) => {
        // console.log(datas);
        let name = '';
        datas
            && datas.forEach(item => {
                if (item.code === dicCode) {
                    name = item.name;
                } else {
                    item.options
                        && item.options.forEach(i => {
                            if (i.code === dicCode) {
                                name = i.name;
                            }
                        });
                }
            });

        return name;
    };

    // 表单切换 - 国籍字典（收款信息）
    useEffect(() => {
        sysdictionaryApi({ dicGroup: 'NATIONALITY' }).then(res => {
            if (res.status_code === 200 && res.data && res.data.length) {
                setNationalityDicList(res.data);
            }
        });
    }, []);

    // 根据关键字查询机构列表
    const requestOrgList = useCallback(
        async keyword => {
            setSearchLoading(true);

            try {
                const res = await getOrganizations({ keyword });
                // console.log("res", res);
                if (!isEmpty(res.data)) {
                    setUnits(res.data);
                } else {
                    setUnits([]);
                }

                setSearchLoading(false);
            } catch (e) {
                setSearchLoading(false);
            }
        },
        [authType],
    );

    // 避免频繁请求
    const debouncedRequestOrgList = useCallback(
        debounce(keyword => {
            if (!!keyword) {
                requestOrgList(keyword);
            } else {
                setUnits([]);
            }
        }, 500),
        [requestOrgList],
    );

    useEffect(() => {
        // console.log(unitSearchKeyword);
        debouncedRequestOrgList(unitSearchKeyword);

        // 组件卸载时取消未执行的防抖函数
        return () => debouncedRequestOrgList.cancel();
    }, [unitSearchKeyword]);

    // 机构名称搜索
    const onChangeUnitSearchKeyword = v => setUnitSearchKeyword(v);

    // 机构选择
    const onChangeUnit = id => {
        // console.log(id);
        if (!!id) {
            const orgType = units?.find(unit => unit.id === id).orgType;

            if (!!orgType) {
                const identity = identityTypes.find(type => type.orgType === orgType)?.code;
                setAuthType(identity);

                const children = unitInfo.childs;
                children?.map(child => {
                    child.memPostCode = child.memPostCode && child.memPostCode.substring(0, 3) === identity ? child.memPostCode : undefined;
                    child.unitPost = child.memPostCode && child.memPostCode.substring(0, 3) === identity ? child.unitPost : '';
                    child.memOfficeCode = child.memOfficeCode && child.memOfficeCode.substring(0, 3) === identity ? child.memOfficeCode : undefined;
                    child.unitOffice = child.memOfficeCode && child.memOfficeCode.substring(0, 3) === identity ? child.unitOffice : '';
                    return child;
                });

                setUnitInfo({ ...unitInfo, memUnitId: id, unitName: units?.find(unit => unit.id === id).name, orgType, authType: identity, childs: children, isNewUnit: unitInfo.isNewUnit });
                unitForm.setFieldsValue({ ...unitInfo, memUnitId: id, unitName: units?.find(unit => unit.id === id).name, orgType, authType: identity, childs: children });
            } else {
                const children = unitInfo.childs;
                children?.map(child => {
                    child.memPostCode = undefined;
                    child.unitPost = '';
                    child.memOfficeCode = undefined;
                    child.unitOffice = '';
                    return child;
                });

                setAuthType('');
                setUnitInfo({ ...unitInfo, memUnitId: id, unitName: units?.find(unit => unit.id === id).name, orgType, isNewUnit: unitInfo.isNewUnit });
                unitForm.setFieldsValue({ ...unitInfo, memUnitId: id, unitName: units?.find(unit => unit.id === id).name, orgType });
            }
        }
    };

    // 点击未分类医院
    // const onClickUnclassifiedHospital = (orgType) => {
    //     // const orgType = units?.find((unit) => unit.id === id).orgType;
    //     if (!orgType) {
    //         message.warning("此机构未分类，请在数据中心 > 机构管理 > 更新机构类型后可选择");
    //     }
    // };

    const onShowCreateUnitModal = () => {
        setVisibleCreateUnitModal(true);
        createUnitForm.setFieldValue('unitName', unitSearchKeyword);
    };

    // 新增单位名称（机构）
    const onCreateUnit = () => {
        createUnitForm.validateFields().then(async values => {
            // console.log(values);
            const res = await organizationsSaveAndUpdateApi({
                name: values.unitName,
                orgType: values.orgType,
            });
            if (!isEmpty(res.data)) {
                setUnits([res.data]);
                // 将新创建的机构ID设置到表单中
                unitForm.setFieldsValue({ memUnitId: res.data.id });

                const identity = identityTypes.find(type => type.orgType === values.orgType)?.code;
                setAuthType(identity);

                setUnitInfo({ ...unitInfo, memUnitId: res.data.id, unitName: values.unitName, orgType: values.orgType, authType: identity, isNewUnit: unitInfo.isNewUnit });

                message.success('机构创建成功');

                createUnitForm.resetFields();
                setVisibleCreateUnitModal(false);
            } else {
                // message.error('机构创建失败，请稍后重试');
            }
        });
    };

    // 取消新增单位名称（机构）
    const onCancelCreateUnit = () => {
        createUnitForm.resetFields();
        setVisibleCreateUnitModal(false);
    };

    // 准备单位所在地信息
    const prepareLocation = unit => {
        let location = '';

        if (unit?.province) {
            location += unit.province;

            // 如果有市，且不是省直辖县级行政区划（北京、上海、天津、重庆），则添加市
            // 避免出现"北京市"为"北京"的情况
            const municipalityRegx = /^(北京|上海|天津|重庆)/;
            if (!!unit?.city && !municipalityRegx.test(unit.province)) {
                location += unit.city;
            }

            // 如果有区县，则添加区县（包括直辖市的区县）
            if (!!unit?.district) {
                location += unit.district;
            }
        }
        return location;
    };

    // 准备机构别称
    const prepareOrgAliase = unit => {
        if (!unit?.aliases) return '';

        try {
            // 解析JSON格式的别称数据
            const aliases = JSON.parse(unit.aliases);
            if (!Array.isArray(aliases) || aliases.length === 0) return '';

            // 分离当前别称和曾用名
            const currentAliases = aliases.filter(alias => !alias.former);
            const formerAliases = aliases.filter(alias => alias.former);

            let aliasText = '';

            // 添加当前别称
            if (currentAliases.length > 0) {
                const currentNames = currentAliases.map(alias => alias.name).join('、');
                aliasText += currentNames;
            }

            // 添加曾用名
            if (formerAliases.length > 0) {
                const formerNames = formerAliases.map(alias => `${alias.name}(曾用名)`).join('、');
                if (!!aliasText) aliasText += '、';
                aliasText += formerNames;
            }

            return aliasText;
        } catch (error) {
            console.error('解析机构别称失败:', error);
            return '';
        }
    };

    // 任期类型切换
    const onCurrentChange = (e, index) => {
        if (e.target.value === 1) {
            const formValues = unitForm.getFieldsValue();
            const childs = formValues.childs || [];

            // 确保索引有效且对象存在
            if (index >= 0 && index < childs.length && childs[index]) {
                childs[index] = {
                    ...childs[index],
                    tenureDateType: '',
                    tenureStartTime: undefined,
                    tenureEndTime: undefined,
                };
                unitForm.setFieldsValue({ childs });
            }
        }
    };

    // 认证字典数据
    const requestAuthDict = async () => {
        const res = await authdictionaryApi();
        if (!isEmpty(res?.options)) {
            const authDictData = res.options;
            authDictData
                && Array.isArray(authDictData)
                && authDictData.forEach(authType => {
                    authType.labels
                        && Array.isArray(authType.labels)
                        && authType.labels.forEach(label => {
                            label.options
                                && Array.isArray(label.options)
                                && label.options.forEach(l => {
                                    if (isEmpty(l.options)) {
                                        delete l.options;
                                    }
                                });
                        });
                });
            setAuthDict(authDictData);
            // console.log("authDictData", authDictData);
        }
    };

    useEffect(() => {
        requestAuthDict(); // 获取认证字典数据
    }, []);

    // 单位任职保存
    const saveUnitItem = index => {
        unitForm.validateFields().then(async values => {
            const unitNew = { ...unitInfo, ...values, isNewUnit: false };

            unitNew.childs.map(item => {
                // 保持为 dayjs/undefined，避免 DatePicker 读取字符串报错；提交时统一转换
                item.unitOffice = item.unitOffice ? item.unitOffice : '';
                item.unitPost = item.unitPost ? item.unitPost : '';
                item.memOfficeCode = item.memOfficeCode ? item.memOfficeCode : '';
                item.memPostCode = item.memPostCode ? item.memPostCode : '';
                return item;
            });

            const experiences = expertForm.getFieldValue('experiences');
            experiences[index] = unitNew;

            // console.log("unitNew", unitNew);

            expertForm.setFieldsValue({ experiences });

            unitForm.resetFields();
            setUnitInfo({});
            setEditIndex(-1);
            setAuthType('');
            setUnits([]);
        });
    };

    // 单位任职 取消
    const cancelUnitItem = index => {
        if (isEmpty(unitInfo) && editIndex === 0) {
            return;
        }

        const employments = [...(expertForm.getFieldValue('experiences') || [])];

        // 判断是否是新增的单位任职
        // 如果是新增的单位任职，或者当前编辑的单位任职没有ID（表示是新增的），则移除
        const currentExperience = employments[index];
        const isNewUnit = unitInfo.isNewUnit || !currentExperience?.id;

        if (isNewUnit) {
            // 新增的单位任职，无论是否填写完整，取消时都应该移除
            employments.splice(index, 1);
            expertForm.setFieldsValue({ experiences: employments });
        }

        // console.log('cancel', expertForm.getFieldsValue().experiences);

        setUnitInfo({});
        unitForm.resetFields();
        setEditIndex(-1);
        setAuthType('');
        setUnits([]);
    };

    // 预览对外展示信息
    const previewExpertInfo = () => {
        const experiences = expertForm.getFieldValue('experiences') || [];
        setViewExpertVisible(true);
    };

    const onCancel = () => {
        if (!isEmbedded) {
            navigate('/dc/experts');
        }
    };

    const handleExpertFormValuesChange = (changedValues, allValues) => {
        if (isProgrammaticFormUpdateRef.current) {
            return;
        }

        userDirtyRef.current = true;
        onDirtyChange?.(true);

        if ('idCard' in changedValues || 'idcardType' in changedValues) {
            const birthDate = extractBirthdayFromIdCard(allValues.idCard, allValues.idcardType);
            if (birthDate) {
                expertForm.setFieldValue('birthday', birthDate);
            }
        }
    };

    const onFinish = async () => {
        try {
            const values = await expertForm.validateFields();
            const expertData = { ...values };
            delete expertData.updateTraces;
            delete expertData.update_traces;
            // 是否禁用不在表单内编辑（列表与抽屉 footer 展示/操作）；提交时沿用服务端状态，新增默认为正常
            expertData.status = expertId ? (expertInfo?.status ?? 0) : 0;

            if (isCnIdCardType(expertData.idcardType)) {
                expertData.nationality = undefined;
                expertData.birthPlace = undefined;
            }
            if (expertData.idCard != null && expertData.idCard !== '' && expertData.idcardType != null && expertData.idcardType !== '') {
                expertData.idCard = normalizeIdCardInput(expertData.idCard, expertData.idcardType);
                const idt = Number(expertData.idcardType);
                if (!Number.isNaN(idt)) {
                    expertData.idcardType = idt;
                }
            }

            if (editIndex !== -1) {
                message.warning('请先保存编辑中的单位任职！');
                return;
            }

            const birthday = expertData?.birthday ? expertData?.birthday.format('YYYY-MM-DD') : '';
            const provinceCode = expertData?.area?.[0] ? expertData.area[0] : '';
            const cityCode = expertData?.area?.[1] ? expertData.area[1] : '';
            const districtCode = expertData?.area?.[2] ? expertData.area[2] : '';
            const expertAvatar = fileList?.[0]?.url ? fileList?.[0].url : '';
            const publicityPhoto = publicityPhotoList?.[0]?.url ? publicityPhotoList?.[0].url : '';
            const introFile = expertData?.introFile ? expertData.introFile : '';

            let newArr;
            let moreArr;

            // antd v6 validateFields 不包含 Form.List 未注册子项数据，
            // 需使用 getFieldValue 从 form store 获取完整 experiences
            const fullExperiences = expertForm.getFieldValue('experiences');
            if (fullExperiences) {
                expertData.experiences = fullExperiences;
            }

            if (expertData?.experiences) {
                newArr = expertData?.experiences?.filter(v => {
                    if (!v?.unitName) {
                        delete v?.unitName;
                    } else if (v.childs) {
                        v.childs = v?.childs?.filter(it => {
                            if (it) {
                                const o = Object.values(it).filter(i => i);
                                if (o.length) {
                                    return it !== undefined;
                                }
                            }
                            return false;
                        });
                    }

                    if (JSON.stringify(v) !== '{}') {
                        return true;
                    }
                    return false;
                });
                newArr = newArr.filter(it => it !== undefined);
            }

            if (expertData?.contactsList) {
                moreArr = expertData.contactsList.filter(v => {
                    if (v) {
                        const o = Object.values(v).filter(i => i);
                        if (o.length) {
                            return true;
                        }
                    }
                    return false;
                });
            }

            if (expertData.mobiles) {
                expertData.memMobile = expertData.mobiles[0]?.memMobile || undefined;
                expertData.otherMobile = expertData.mobiles[1] ? expertData.mobiles[1]?.otherMobile : undefined;
                expertData.mobiles = undefined;
            }

            if (expertData?.experiences) expertData.experiences = newArr;
            if (expertData?.contactsList) expertData.contactsList = moreArr;

            // 提交前将任职日期统一格式化为字符串（避免 DatePicker 读取到字符串时的 dayjs 适配错误）
            if (Array.isArray(expertData?.experiences)) {
                expertData.experiences = expertData.experiences.map(exp => {
                    const expCopy = { ...exp };
                    if (Array.isArray(expCopy.childs)) {
                        expCopy.childs = expCopy.childs.map(child => {
                            const childCopy = { ...child };
                            const pickerType = childCopy.tenureDateType;
                            const toStr = d => (d && dayjs(d).isValid() ? formatDateByPickerTypeForSave(d, pickerType) : null);
                            childCopy.tenureStartTime = toStr(childCopy.tenureStartTime);
                            childCopy.tenureEndTime = toStr(childCopy.tenureEndTime);
                            return childCopy;
                        });
                    }
                    return expCopy;
                });
            }

            let data = {
                ...expertData,
                birthday,
                provinceCode,
                cityCode,
                districtCode,
                expertAvatar,
                publicityPhoto,
                // 荣誉头衔以 JSON 字符串数组保存
                honoraryTitles: JSON.stringify((expertData?.honoraryTitles || []).filter(Boolean)),
            };
            console.log(data);

            data.introFile = introFile;

            if (expertId) {
                const keysToUpdate = [];
                if (traceBaselineRef.current) {
                    const bl = traceBaselineRef.current;
                    const curIntro = normalizeExpertIntroHtmlForCompare(prodDetails ?? expertData.memIntroduction);
                    if (curIntro !== bl.introNorm || (introFile || '') !== bl.introFile) {
                        keysToUpdate.push('intro');
                    }
                    if (JSON.stringify(data.experiences || []) !== bl.experiencesKey) {
                        keysToUpdate.push('unit');
                    }
                    const laborfeeCompareValues = {
                        ...expertData,
                        birthday: birthday ? dayjs(birthday, 'YYYY-MM-DD') : undefined,
                    };
                    if (serializeLaborfeeFieldsForCompare(laborfeeCompareValues) !== bl.laborfeeKey) {
                        keysToUpdate.push('laborfee');
                    }
                    const mergedJson = mergeExpertUpdateTraceEntriesForEdit(expertInfo?.updateTraces, keysToUpdate);
                    if (mergedJson) {
                        data.updateTraces = mergedJson;
                    }
                }
                data = { ...data, id: expertId };
                const afterData = expertForm.getFieldsValue();
                try {
                    const res = await editInfoApi(data);
                    if (res.status_code === 200) {
                        await logExpertUpdate(expertId, beforeDataRefs.current, afterData, true);
                        if (isEmbedded) {
                            onStatusChange?.(data.status);
                            userDirtyRef.current = false;
                            onDirtyChange?.(false);
                            await onSaveSuccess?.();
                        } else {
                            navigate('/dc/experts');
                        }
                    } else {
                        await logExpertUpdate(expertId, beforeDataRefs.current, afterData, false, res.message);
                        message.error(res.message || '专家信息保存失败');
                    }
                } catch (err) {
                    const errMsg = typeof err === 'string' ? err : err?.message;
                    await logExpertUpdate(expertId, beforeDataRefs.current, afterData, false, errMsg);
                }
            } else {
                const afterData = expertForm.getFieldsValue();
                try {
                    const res = await saveExpertApi(data);
                    if (res.status_code === 200) {
                        const newExpertId = res.data?.id || 0;
                        await logExpertCreate(newExpertId, afterData, true);
                        if (isEmbedded) {
                            userDirtyRef.current = false;
                            onDirtyChange?.(false);
                            await onSaveSuccess?.();
                        } else {
                            navigate('/dc/experts');
                        }
                    } else {
                        await logExpertCreate(0, afterData, false, res.message);
                        message.error(res.message || '专家信息保存失败');
                    }
                } catch (err) {
                    const errMsg = typeof err === 'string' ? err : err?.message;
                    await logExpertCreate(0, afterData, false, errMsg);
                }
            }
        } catch (err) {
            scrollToFirstFormItemHasError();
            const firstFieldMsg = err?.errorFields?.[0]?.errors?.[0];
            message.error(firstFieldMsg || err?.message || '专家信息不完整，保存失败！');
        }
    };

    useImperativeHandle(ref, () => ({
        submit: onFinish,
    }));

    useEffect(() => {
        if (!isEmbedded) {
            document.title = expertId ? '编辑专家-专家库' : '新增专家-专家库';
        }
    }, [expertId, isEmbedded]);

    return (
        <>
            <div id="expert-container" className="expert-form-shell">
                <Row justify="center" className="expert-form-shell__row">
                    <Col flex="0 1 1200px" className="expert-form-shell__col">
                        <div className="form-container">
                            <Form form={expertForm} name="expertForm" {...pageFormItemLayout} onValuesChange={handleExpertFormValuesChange}>
                                <div id="section-basic" className="form-subtitle">
                                    基本信息{' '}
                                </div>

                                <Form.Item
                                    name="memUsername"
                                    label="姓名"
                                    rules={[
                                        { required: true, message: '姓名不能为空' },
                                        { pattern: /^.{2,50}$/, message: '姓名2-50个字符' },
                                    ]}
                                    extra={
                                        someNameList
                                        && !!someNameList.length && (
                                            <Alert
                                                type="warning"
                                                style={{ margin: '8px 0' }}
                                                title={
                                                    <>
                                                        <div className="some-name-title">专家库已存在以下同名专家：</div>
                                                        <div className="some-name-wrap">
                                                            {someNameList.map((row, rowIndex) => (
                                                                <div className="some-name-list" key={rowIndex}>
                                                                    <div className="name">{row.memUsername}</div>
                                                                    <div className="mobile">{row.memMobile}</div>
                                                                    <div className="post">
                                                                        {row.ssoMemberWorkExperiencesEntities && row.ssoMemberWorkExperiencesEntities.length
                                                                            ? row.ssoMemberWorkExperiencesEntities.map((item, index) => (
                                                                                  <span key={index}>{`${item.unitName ? item.unitName + ' ' : ''}${item.unitOffice ? item.unitOffice + ' ' : ''}${item.unitPost ? item.unitPost + ' ' : ''}${item.unitJobTitle ? item.unitJobTitle : ''}`}</span>
                                                                              ))
                                                                            : null}
                                                                    </div>
                                                                    <Button type="link" onClick={() => viewDetail(row)}>
                                                                        查看详情
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                }
                                            />
                                        )
                                    }
                                >
                                    <Input placeholder="请输入姓名" maxLength={50} onBlur={searchName} showCount />
                                </Form.Item>

                                {canViewMobile && (
                                    <Form.Item noStyle shouldUpdate>
                                        {({ getFieldValue }) => {
                                            const hasMobile = getFieldValue('hasMobile');
                                            const phoneUpdateTrace = getExpertUpdateTrace(expertInfo, 'phone');
                                            return (
                                                <>
                                                    <Form.Item
                                                        label="手机号"
                                                        className={hasMobile && !formalMobile ? 'required' : ''}
                                                        extra={
                                                            !!getFieldValue('hasMobile') && (
                                                                <>
                                                                    {!formalMobile ? (
                                                                        <span>该手机号为“主手机号”，“我的项目”相关活动及信息关联该手机号，请确保准确性。</span>
                                                                    ) : (
                                                                        <span>
                                                                            该手机号为“主手机号”，“我的项目”相关活动及信息关联该手机号，请确保准确性，如有出入请
                                                                            <Button type="link" onClick={() => openChangeExpertMobileModal()} style={{ margin: 0, padding: 0 }}>
                                                                                更换手机号
                                                                            </Button>
                                                                            。
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )
                                                        }
                                                    >
                                                        <Row gutter={8} align="middle" wrap>
                                                            {!formalMobile && (
                                                                <Col style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Form.Item name="hasMobile" valuePropName="checked" noStyle initialValue={!expertId || expertInfo?.hasMobile}>
                                                                        <Switch checkedChildren="有" unCheckedChildren="无" />
                                                                    </Form.Item>
                                                                </Col>
                                                            )}

                                                            <Col flex="1" style={{ minWidth: 0 }}>
                                                                <div className="expert-form-mobile-inline">
                                                                    {!!getFieldValue('hasMobile') && (
                                                                        <div className="expert-form-mobile-primary">
                                                                            <Form.Item
                                                                                name="memMobile"
                                                                                noStyle
                                                                                normalize={value => (value ? value.replace(/\s+/g, '') : value)}
                                                                                rules={[
                                                                                    { required: hasMobile, message: '手机号不能为空' },
                                                                                    { pattern: /^1(?![01])\d{10}$/, message: '填写的手机号格式不正确' },
                                                                                ]}
                                                                            >
                                                                                {!!formalMobile ? formalMobile : <Input placeholder="请输入手机号" style={{ width: 200 }} />}
                                                                            </Form.Item>
                                                                        </div>
                                                                    )}
                                                                    {!!getFieldValue('hasMobile') && phoneUpdateTrace && (
                                                                        <span className="expert-phone-update-trace">
                                                                            <i className="bi bi-clock" />
                                                                            <span>
                                                                                最后更新：{phoneUpdateTrace.updatedAt}
                                                                                {phoneUpdateTrace.updatedByLabel ? ` ${phoneUpdateTrace.updatedByLabel}` : ''}
                                                                            </span>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Form.Item>

                                                    {!!getFieldValue('hasMobile') && (
                                                        <Form.Item label="其他手机号" name="otherMobile" normalize={value => (value ? value.replace(/\s+/g, '') : value)} rules={[{ pattern: /^1(?![01])\d{10}$/, message: '填写的手机号格式不正确' }]}>
                                                            <Input placeholder="请输入手机号" style={{ width: 200 }} />
                                                        </Form.Item>
                                                    )}
                                                </>
                                            );
                                        }}
                                    </Form.Item>
                                )}

                                <Form.Item
                                    name="expertLevel"
                                    label="专家级别"
                                    rules={[{ required: true, message: '未选专家级别' }]}
                                    extra={
                                        <div className="expert-level-instruction vertical-scrollbar">
                                            {expertLevels
                                                .filter(el => el.level !== 0 || el.level !== 12)
                                                .map((el, idx) => (
                                                    <div key={idx} className="expert-level">
                                                        <b>
                                                            <Text type="warning">{el.label}</Text>
                                                        </b>{' '}
                                                        {el.desc}
                                                        {idx < expertLevels.length - 2 ? <br /> : ''}
                                                    </div>
                                                ))}
                                        </div>
                                    }
                                >
                                    <Select placeholder="请选择专家级别" showSearch={{ optionFilterProp: 'children' }} styles={{ popup: { root: { maxHeight: 600, overflow: 'auto' } } }}>
                                        {expertLevels.map((item, index) => (
                                            <Select.Option key={index} value={item.level}>
                                                {item.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item label="单位任职" required>
                                    <Form.List name="experiences" initialValue={[{ memUnitId: undefined, unitName: '' }]}>
                                        {(fields, { add, remove, move }) => (
                                            <>
                                                <div className="unit-employment-btns unit-employment-btns--with-trace">
                                                    <div className="unit-employment-btns__left">
                                                        {editIndex === -1 && (
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                onClick={() => addUnitEmployment()}
                                                                disabled={fields.length >= 10}
                                                                icon={
                                                                    <span className="anticon">
                                                                        <i className="bi bi-plus-circle"></i>
                                                                    </span>
                                                                }
                                                                style={{ padding: 4, marginBottom: 8 }}
                                                            >
                                                                <span>新增单位任职</span>
                                                            </Button>
                                                        )}

                                                        {fields.length > 0 && editIndex === -1 && (
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                onClick={previewExpertInfo}
                                                                icon={
                                                                    <span className="anticon">
                                                                        <i className="bi bi-eye"></i>
                                                                    </span>
                                                                }
                                                                style={{ padding: 4, marginBottom: 8 }}
                                                            >
                                                                <span>预览对外展示信息</span>
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {unitUpdateTrace && (
                                                        <span className="expert-phone-update-trace expert-unit-update-trace">
                                                            <i className="bi bi-clock" />
                                                            <span>
                                                                最后更新：{unitUpdateTrace.updatedAt} {unitUpdateTrace.updatedByLabel ? ` ${unitUpdateTrace.updatedByLabel}` : ''}
                                                            </span>
                                                        </span>
                                                    )}
                                                </div>

                                                {fields.map(({ key, name, ...restField }, index) =>
                                                    index !== editIndex ? (
                                                        <div className="unit-employment-show-item" key={key}>
                                                            <Row gutter={10}>
                                                                <Col span={14}>
                                                                    <h3>{watchedExperiences?.[index]?.unitName}</h3>
                                                                </Col>
                                                                <Col span={10} className="actions">
                                                                    <div className="move-btn">
                                                                        {index !== 0 && (
                                                                            <Button type="link" size="small" onClick={() => move(index, index - 1)}>
                                                                                上移
                                                                            </Button>
                                                                        )}
                                                                        {index !== fields.length - 1 && (
                                                                            <Button type="link" size="small" onClick={() => move(index, index + 1)}>
                                                                                下移
                                                                            </Button>
                                                                        )}
                                                                        <Button type="link" size="small" onClick={() => editUnitEmployment(index)}>
                                                                            编辑
                                                                        </Button>

                                                                        {fields.length > 1 && (
                                                                            <>
                                                                                {expertId && watchedExperiences?.[index]?.id ? (
                                                                                    <Popconfirm
                                                                                        title="确定删除该单位任职么？"
                                                                                        onConfirm={() => {
                                                                                            const id = watchedExperiences?.[index]?.id;
                                                                                            delPoistionApi({ id }).then(res => {
                                                                                                if (res.status_code === 200) {
                                                                                                    remove(name);
                                                                                                    message.success('删除单位任职成功');
                                                                                                }
                                                                                            });
                                                                                        }}
                                                                                        okText="确定"
                                                                                        cancelText="取消"
                                                                                    >
                                                                                        <Button type="link">移除</Button>
                                                                                    </Popconfirm>
                                                                                ) : (
                                                                                    <Button type="link" onClick={() => remove(name)}>
                                                                                        移除
                                                                                    </Button>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            {watchedExperiences?.[index]?.childs && (
                                                                <div className="employment-list">
                                                                    {watchedExperiences[index].childs.map((child, idx) => (
                                                                        <div className="employment-item" key={child.id ?? `employment-${index}-${idx}`} style={child.current === 0 ? { paddingTop: 16 } : {}}>
                                                                            <Row>
                                                                                {(child.memOfficeCode || child.unitOffice)
                                                                                    && (watchedExperiences[index].authType === 'A01' ? (
                                                                                        <Col span={10}>
                                                                                            <p>
                                                                                                <span className="label">科室：</span>
                                                                                                {!child.unitOffice ? (
                                                                                                    <Tooltip title={`该科室为标准科室`}>
                                                                                                        <span>{child.unitOffice ? child.unitOffice : getDicName(authDict?.find(item => item.code === 'A01')?.labels?.find(l => l.code === 'A01-KSH')?.options, child.memOfficeCode)}</span>
                                                                                                        {!child.unitOffice && <CheckCircleFill className="expert-form-success-icon" />}
                                                                                                    </Tooltip>
                                                                                                ) : (
                                                                                                    <Tooltip title={`默认展示显示科室“${child.unitOffice}”，对应标准科室“${getDicName(authDict?.find(item => item.code === 'A01')?.labels?.find(l => l.code === 'A01-KSH')?.options, child.memOfficeCode)}”`}>
                                                                                                        <span>{child.unitOffice ? child.unitOffice : getDicName(authDict?.find(item => item.code === 'A01')?.labels?.find(l => l.code === 'A01-KSH')?.options, child.memOfficeCode)}</span>
                                                                                                    </Tooltip>
                                                                                                )}

                                                                                                {child.memOfficeCode && child.unitOffice && (
                                                                                                    <Text type="secondary" style={{ marginLeft: 4 }}>
                                                                                                        [标准科室：{getDicName(authDict?.find(item => item.code === 'A01')?.labels?.find(l => l.code === 'A01-KSH')?.options, child.memOfficeCode)}]
                                                                                                    </Text>
                                                                                                )}
                                                                                            </p>
                                                                                        </Col>
                                                                                    ) : (
                                                                                        <Col span={10}>
                                                                                            <span className="label">部门：{child.unitOffice}</span>
                                                                                        </Col>
                                                                                    ))}

                                                                                <Col span={10}>
                                                                                    <p>
                                                                                        <span className="label">职务：</span>
                                                                                        {!child.unitPost ? (
                                                                                            <Tooltip title={`该职务为标准职务`}>
                                                                                                <span className="name">
                                                                                                    {child.unitPost
                                                                                                        ? child.unitPost
                                                                                                        : getDicName(authDict?.find(item => item.code === child.memPostCode?.substring(0, 3))?.labels?.find(l => l.code === `${child.memPostCode?.substring(0, 3)}-ZHW`)?.options, child.memPostCode)}
                                                                                                </span>
                                                                                                {!!child.memPostCode && <CheckCircleFill className="expert-form-success-icon" />}
                                                                                            </Tooltip>
                                                                                        ) : (
                                                                                            <Tooltip
                                                                                                title={`默认展示显示职务"${child.unitPost}"，对应标准职务"${getDicName(authDict?.find(item => item.code === watchedExperiences[index].authType)?.labels?.find(l => l.code === `${watchedExperiences[index].authType}-ZHW`)?.options, child.memPostCode)}"`}
                                                                                            >
                                                                                                <span className="name">
                                                                                                    {child.unitPost
                                                                                                        ? child.unitPost
                                                                                                        : getDicName(authDict?.find(item => item.code === watchedExperiences[index].authType)?.labels?.find(l => l.code === `${watchedExperiences[index].authType}-ZHW`)?.options, child.memPostCode)}
                                                                                                </span>
                                                                                                {child.memPostCode && child.unitPost && (
                                                                                                    <Text type="secondary" style={{ marginLeft: 4 }}>
                                                                                                        [标准职务：{getDicName(authDict?.find(item => item.code === child.memPostCode?.substring(0, 3))?.labels?.find(l => l.code === `${child.memPostCode?.substring(0, 3)}-ZHW`)?.options, child.memPostCode)}]
                                                                                                    </Text>
                                                                                                )}
                                                                                            </Tooltip>
                                                                                        )}
                                                                                    </p>
                                                                                </Col>
                                                                            </Row>

                                                                            {child.current === 0 && (
                                                                                <div className="employment-item-date">
                                                                                    历任（
                                                                                    {!child.tenureDateType
                                                                                        ? '任期未知'
                                                                                        : child.tenureDateType === 'year'
                                                                                          ? child.tenureEndTime
                                                                                              ? `${formatDateForDisplay(child.tenureEndTime, 'year')}卸任`
                                                                                              : `${formatDateForDisplay(child.tenureStartTime, 'year')}上任`
                                                                                          : child.tenureEndTime && child.tenureStartTime
                                                                                            ? `${formatDateForDisplay(child.tenureStartTime, 'month')}~${formatDateForDisplay(child.tenureEndTime, 'month')}`
                                                                                            : child.tenureEndTime
                                                                                              ? `${formatDateForDisplay(child.tenureEndTime, 'month')}卸任`
                                                                                              : `${formatDateForDisplay(child.tenureStartTime, 'month')}上任`}
                                                                                    ）
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="unit-employment-item" key={key}>
                                                            <Form form={unitForm} name="unitForm" layout="inline" component={false}>
                                                                <div className="unit-employment-body">
                                                                    <Row>
                                                                        <Col span={18}>
                                                                            <Form.Item className="section" name="memUnitId" label="单位名称" rules={[{ required: true, message: '单位名称不能为空' }]}>
                                                                                <Select
                                                                                    optionLabelProp="label"
                                                                                    placeholder="请输入单位名称关键字检索"
                                                                                    suffixIcon={null}
                                                                                    allowClear
                                                                                    defaultActiveFirstOption={false}
                                                                                    onChange={onChangeUnit}
                                                                                    showSearch={{
                                                                                        filterOption: false,
                                                                                        onSearch: onChangeUnitSearchKeyword,
                                                                                    }}
                                                                                    styles={{ popup: { root: { width: '500px !important', minWidth: 500 } } }}
                                                                                    getPopupContainer={triggerNode => triggerNode.parentNode}
                                                                                    popupRender={menu => (
                                                                                        <>
                                                                                            {menu}
                                                                                            {!!units && units.some(unit => !unit.orgType) && <div className="expert-form-unit-org-warning">未分类的机构不可选择，请在数据中心-机构管理中完善后再选择</div>}
                                                                                        </>
                                                                                    )}
                                                                                    notFoundContent={
                                                                                        searchLoading ? (
                                                                                            <div style={{ display: 'flex', justifyContent: 'center', padding: '5px 0' }}>
                                                                                                <Spin />
                                                                                            </div>
                                                                                        ) : (
                                                                                            unitSearchKeyword && (
                                                                                                <div className="emptyUnitSelectBox">
                                                                                                    <span>
                                                                                                        没有找到你想要的单位名称？<a onClick={onShowCreateUnitModal}>点击添加</a>
                                                                                                    </span>
                                                                                                </div>
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {!!units
                                                                                        && units.map(unit => (
                                                                                            <Select.Option value={unit.id} key={unit.id} label={unit.name} disabled={!unit.orgType}>
                                                                                                <div className={`unitSelectBox${!unit.orgType ? ' unitSelectBox--disabled' : ''}`}>
                                                                                                    <div className="unitName">{unit.name}</div>

                                                                                                    <div className="unitInfo">
                                                                                                        {!!prepareMedLevelGrade(unit) && <span style={{ marginRight: 10 }}>{prepareMedLevelGrade(unit)}</span>}
                                                                                                        <span style={{ marginRight: 10 }}>{orgTypeList.find(item => item.value === unit.orgType)?.label}</span>
                                                                                                        {!!prepareLocation(unit) && <span>{prepareLocation(unit)}</span>}
                                                                                                    </div>

                                                                                                    {!!prepareOrgAliase(unit) && (
                                                                                                        <div className="unitAliase">
                                                                                                            <span>{prepareOrgAliase(unit)}</span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            </Select.Option>
                                                                                        ))}
                                                                                </Select>
                                                                            </Form.Item>
                                                                        </Col>
                                                                    </Row>

                                                                    {!!unitForm.getFieldValue('memUnitId') && (
                                                                        <Form.List
                                                                            name="childs"
                                                                            initialValue={[
                                                                                {
                                                                                    current: 1,
                                                                                    unitOffice: '',
                                                                                    unitPost: '',
                                                                                    memPostCode: undefined,
                                                                                    memOfficeCode: undefined,
                                                                                    tenureEndTime: undefined,
                                                                                    tenureStartTime: undefined,
                                                                                    tenureDateType: '',
                                                                                },
                                                                            ]}
                                                                        >
                                                                            {(childs, { add: addtest, remove: removetest, move: moveChild }) => (
                                                                                <>
                                                                                    <Button
                                                                                        type="link"
                                                                                        onClick={() => addtest({ current: 1 }, childs.length)}
                                                                                        disabled={childs.length >= 5}
                                                                                        icon={
                                                                                            <span className="anticon">
                                                                                                <PlusCircle />
                                                                                            </span>
                                                                                        }
                                                                                        style={{ padding: 4 }}
                                                                                    >
                                                                                        添加任职
                                                                                    </Button>

                                                                                    {childs.map(({ key: childKey, name: childName, ...restField }, idx) => (
                                                                                        <div className="add-post-container" key={childKey}>
                                                                                            {/* 保留子记录 id/pid，确保后端识别为更新而非新增 */}
                                                                                            <Form.Item {...restField} name={[childName, 'id']} hidden noStyle>
                                                                                                <Input />
                                                                                            </Form.Item>
                                                                                            <Form.Item {...restField} name={[childName, 'pid']} hidden noStyle>
                                                                                                <Input />
                                                                                            </Form.Item>
                                                                                            <div className="post-operation-btns">
                                                                                                {idx !== 0 && <Button type="link" className="dark" icon={<ArrowUpCircle />} style={{ width: 'auto' }} onClick={() => moveChild(idx, idx - 1)} />}

                                                                                                {idx !== childs.length - 1 && <Button type="link" className="dark" icon={<ArrowDownCircle />} style={{ width: 'auto' }} onClick={() => moveChild(idx, idx + 1)} />}

                                                                                                {idx !== 0 && (
                                                                                                    <>
                                                                                                        {expertId && watchedExperiences?.[index]?.childs?.[idx]?.id ? (
                                                                                                            <Popconfirm
                                                                                                                title="确定删除该任职么？"
                                                                                                                onConfirm={() => {
                                                                                                                    const id = watchedExperiences?.[index]?.childs?.[idx]?.id;
                                                                                                                    delPoistionApi({ id }).then(res => {
                                                                                                                        if (res.status_code === 200) {
                                                                                                                            removetest(childName);
                                                                                                                            message.success('删除任职成功');
                                                                                                                        }
                                                                                                                    });
                                                                                                                }}
                                                                                                                okText="确定"
                                                                                                                cancelText="取消"
                                                                                                            >
                                                                                                                <Button type="link" className="dark" icon={<XCircle />} style={{ width: 'auto' }} />
                                                                                                            </Popconfirm>
                                                                                                        ) : (
                                                                                                            <Button type="link" className="dark" icon={<XCircle />} style={{ width: 'auto' }} onClick={() => removetest(childName)} />
                                                                                                        )}
                                                                                                    </>
                                                                                                )}
                                                                                            </div>

                                                                                            <div className="position">
                                                                                                <div className="position-field-box">
                                                                                                    <Form.Item {...restField} label="标准职务" name={[childName, 'memPostCode']} className="position-item" rules={[{ required: true, message: '职务不能为空' }]} style={{ width: 300 }}>
                                                                                                        <Select
                                                                                                            placeholder="请选择您的职务"
                                                                                                            options={authDict?.find(item => item.code === authType)?.labels?.find(l => l.code === `${authType}-ZHW`)?.options}
                                                                                                            fieldNames={{ label: 'name', value: 'code' }}
                                                                                                            showSearch={{
                                                                                                                optionFilterProp: 'children',
                                                                                                                filterOption: (input, option) => (option?.name ?? '').includes(input),
                                                                                                            }}
                                                                                                            allowClear
                                                                                                            notFoundContent="未找到该职务，若无相近职务请选择其他，并在显示职务中填写正确名称。"
                                                                                                        />
                                                                                                    </Form.Item>
                                                                                                    <Form.Item {...restField} label="显示职务" name={[childName, 'unitPost']} className="position-item" style={{ width: 300 }}>
                                                                                                        <Input placeholder="请输入显示职务" />
                                                                                                    </Form.Item>
                                                                                                </div>

                                                                                                <div className="position-field-extra">平台默认显示所选“标准职务”。若填写了“显示职务”，则优先展示填写的职务名称。</div>

                                                                                                {authType === 'A01' ? (
                                                                                                    <>
                                                                                                        <div className="dept-field-box">
                                                                                                            <Form.Item {...restField} label="标准科室" name={[childName, 'memOfficeCode']} className="position-item" style={{ width: 300 }}>
                                                                                                                <Select
                                                                                                                    placeholder="请选择您的科室"
                                                                                                                    options={authDict?.find(item => item.code === authType)?.labels?.find(l => l.code === `${authType}-KSH`)?.options}
                                                                                                                    fieldNames={{ label: 'name', value: 'code' }}
                                                                                                                    showSearch={{
                                                                                                                        optionFilterProp: 'children',
                                                                                                                        filterOption: (input, option) => (option?.name ?? '').includes(input),
                                                                                                                    }}
                                                                                                                    allowClear
                                                                                                                    notFoundContent="未找到该科室，若无相近科室请选择其他，并在显示科室中填写正确名称。"
                                                                                                                />
                                                                                                            </Form.Item>

                                                                                                            <Form.Item {...restField} label="显示科室" name={[childName, 'unitOffice']} className="position-item" style={{ width: 300 }}>
                                                                                                                <Input placeholder="请输入显示科室" />
                                                                                                            </Form.Item>
                                                                                                        </div>

                                                                                                        <div className="dept-field-extra">平台默认显示所选“标准科室”。若填写了“显示科室”，则优先展示填写的科室名称。</div>
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <>
                                                                                                        <div className="dept-field-box">
                                                                                                            <Form.Item {...restField} label="所在部门" name={[childName, 'unitOffice']} className="position-item" style={{ width: 300 }}>
                                                                                                                <Input placeholder="请输入部门" />
                                                                                                            </Form.Item>
                                                                                                        </div>
                                                                                                    </>
                                                                                                )}

                                                                                                <div className="deployment-status-field-box">
                                                                                                    <Form.Item label="任职状态" {...restField} name={[childName, 'current']} rules={[{ required: true, message: '请选择任职状态' }]} style={{ marginBottom: 0, marginRight: 0 }}>
                                                                                                        <Radio.Group onChange={e => onCurrentChange(e, idx)}>
                                                                                                            <Radio value={1}>现任</Radio>
                                                                                                            <Radio value={0}>历任</Radio>
                                                                                                        </Radio.Group>
                                                                                                    </Form.Item>

                                                                                                    <Form.Item noStyle shouldUpdate>
                                                                                                        {({ getFieldValue }) => {
                                                                                                            const currentStatus = getFieldValue(['childs', idx, 'current']);
                                                                                                            return currentStatus === 0 ? (
                                                                                                                <>
                                                                                                                    <Form.Item {...restField} name={[childName, 'tenureDateType']} style={{ width: 120 }}>
                                                                                                                        <Select options={datePickerTypes} placeholder="选择任期类型" />
                                                                                                                    </Form.Item>

                                                                                                                    <Form.Item noStyle shouldUpdate>
                                                                                                                        {({ getFieldValue }) => {
                                                                                                                            const dateType = getFieldValue(['childs', idx, 'tenureDateType']);
                                                                                                                            const startTimeValue = getFieldValue(['childs', idx, 'tenureStartTime']);
                                                                                                                            const endTimeValue = getFieldValue(['childs', idx, 'tenureEndTime']);
                                                                                                                            const startTime = startTimeValue ? startTimeValue : undefined;
                                                                                                                            const endTime = endTimeValue ? endTimeValue : undefined;

                                                                                                                            return (
                                                                                                                                !!dateType && (
                                                                                                                                    <>
                                                                                                                                        <Form.Item
                                                                                                                                            {...restField}
                                                                                                                                            name={[childName, 'tenureStartTime']}
                                                                                                                                            validateStatus={(() => {
                                                                                                                                                // 如果任期类型不为空，开始时间和结束时间至少需要一个
                                                                                                                                                if (dateType && !startTimeValue && !endTimeValue) {
                                                                                                                                                    return 'error';
                                                                                                                                                }
                                                                                                                                                return '';
                                                                                                                                            })()}
                                                                                                                                            rules={[
                                                                                                                                                {
                                                                                                                                                    validator: (_, value) => {
                                                                                                                                                        // 如果任期类型不为空，开始时间和结束时间至少需要一个
                                                                                                                                                        if (dateType && !value && !endTimeValue) {
                                                                                                                                                            message.warning('请至少选择开始时间或结束时间');
                                                                                                                                                            return Promise.reject('');
                                                                                                                                                        }
                                                                                                                                                        return Promise.resolve();
                                                                                                                                                    },
                                                                                                                                                },
                                                                                                                                            ]}
                                                                                                                                            style={{ marginRight: 8, marginBottom: 0 }}
                                                                                                                                        >
                                                                                                                                            <DatePicker picker={dateType || 'date'} placeholder="开始日期" />
                                                                                                                                        </Form.Item>
                                                                                                                                        <span>至</span>
                                                                                                                                        <Form.Item
                                                                                                                                            {...restField}
                                                                                                                                            name={[childName, 'tenureEndTime']}
                                                                                                                                            validateStatus={(() => {
                                                                                                                                                // 如果任期类型不为空，开始时间和结束时间至少需要一个
                                                                                                                                                if (dateType && !endTimeValue && !startTimeValue) {
                                                                                                                                                    return 'error';
                                                                                                                                                }
                                                                                                                                                return '';
                                                                                                                                            })()}
                                                                                                                                            rules={[
                                                                                                                                                {
                                                                                                                                                    validator: (_, value) => {
                                                                                                                                                        // 如果任期类型不为空，开始时间和结束时间至少需要一个
                                                                                                                                                        if (dateType && !value && !startTimeValue) {
                                                                                                                                                            message.warning('请至少选择开始时间或结束时间');
                                                                                                                                                            return Promise.reject('');
                                                                                                                                                        }
                                                                                                                                                        return Promise.resolve();
                                                                                                                                                    },
                                                                                                                                                },
                                                                                                                                            ]}
                                                                                                                                            style={{ width: 160, marginBottom: 0 }}
                                                                                                                                        >
                                                                                                                                            <DatePicker picker={dateType || 'date'} placeholder="结束日期" style={{ marginLeft: 8 }} />
                                                                                                                                        </Form.Item>
                                                                                                                                    </>
                                                                                                                                )
                                                                                                                            );
                                                                                                                        }}
                                                                                                                    </Form.Item>
                                                                                                                </>
                                                                                                            ) : null;
                                                                                                        }}
                                                                                                    </Form.Item>
                                                                                                </div>

                                                                                                <Form.Item noStyle shouldUpdate>
                                                                                                    {({ getFieldValue }) => {
                                                                                                        const currentStatus = getFieldValue(['childs', idx, 'current']);
                                                                                                        const dateType = getFieldValue(['childs', idx, 'tenureDateType']);
                                                                                                        return currentStatus === 0 && (dateType === 'year' || dateType === 'month') ? <div className="current-field-extra">上任或卸任日期，仅需填写已知项即可。</div> : null;
                                                                                                    }}
                                                                                                </Form.Item>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </>
                                                                            )}
                                                                        </Form.List>
                                                                    )}

                                                                    <div className="unit-employment-footer">
                                                                        <Button type="default" onClick={() => cancelUnitItem(index)}>
                                                                            取消
                                                                        </Button>
                                                                        <Button type="primary" onClick={() => saveUnitItem(index)}>
                                                                            确认
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </Form>
                                                        </div>
                                                    ),
                                                )}
                                            </>
                                        )}
                                    </Form.List>
                                </Form.Item>

                                <Form.Item name="memJobtitle" label="职称" extra="多职称用分号（；）隔开">
                                    <Input placeholder="请输入职称" />
                                </Form.Item>

                                <Form.Item name="memGender" label="性别">
                                    <Radio.Group>
                                        <Radio value={1}>男</Radio>
                                        <Radio value={0}>女</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item label="个人介绍" name="memIntroduction">
                                    <div className="expert-intro-editor-wrap">
                                        <RichEditor id="expert-legacy-intro" mode="full" value={prodDetails} onChange={onProdDetailsChange} imageUploadHandler={expertIntroImageUploadHandler} height={300} />
                                        {introUpdateTrace && (
                                            <div className="expert-intro-update-trace expert-phone-update-trace">
                                                <i className="bi bi-clock" />
                                                <span>
                                                    最后更新：{introUpdateTrace.updatedAt}
                                                    {introUpdateTrace.updatedByLabel ? ` ${introUpdateTrace.updatedByLabel}` : ''}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </Form.Item>

                                <Form.Item label="简介附件" name="introFile" extra="文件大小不超过 50MB，支持 DOC/DOCX、PPT/PPTX、PDF、JPG/PNG、ZIP/RAR 格式">
                                    <Space size={16}>
                                        <Upload accept=".doc,.docx,.ppt,.pptx,.pdf,.jpg,.jpeg,.png,.zip,.rar" showUploadList={false} customRequest={handleIntroFileUpload} beforeUpload={introFileBeforeUpload}>
                                            <Button icon={<i className="bi bi-upload"></i>}>{introFileInfo?.url ? '更换附件' : '上传附件'}</Button>
                                        </Upload>

                                        {introFileInfo?.url && (
                                            <Button type="link" onClick={handleIntroFileDownload} loading={introFileDownloading}>
                                                下载简介附件
                                            </Button>
                                        )}
                                    </Space>
                                </Form.Item>

                                <Form.Item label="个人头像" name="avatar" extra={<>用于项目嘉宾头像、邀请函头像展示等场景；支持JPG/PNG格式，建议不小于500&times;500px，大小不超过10MB</>}>
                                    <div className="expert-avatar-container">
                                        <div className="expert-avatar-box">
                                            <ImgCrop aspect={1 / 1} rotationSlider minZoom={0.1} maxZoom={2} beforeCrop={async file => await beforeCropCheck(file, true)}>
                                                <Upload customRequest={upload} listType="picture-card" fileList={fileList} onRemove={onRemove} onPreview={handlePreview} beforeUpload={beforeUpload}>
                                                    {!fileList.length && (
                                                        <div>
                                                            <i className="bi bi-plus"></i>
                                                            <div style={{ marginTop: 8 }}>上传头像</div>
                                                        </div>
                                                    )}
                                                </Upload>
                                            </ImgCrop>
                                        </div>

                                        <div className="expert-avatar-extra">{/*{!!fileList.length && <Button type="link">查看原图</Button>}*/}</div>
                                    </div>

                                    <Modal open={previewOpen} footer={null} onCancel={previewCancel} className="preview-modal">
                                        <img alt="example" src={previewImage} className="expert-image" />
                                    </Modal>
                                </Form.Item>

                                <Form.Item
                                    label="宣传照片"
                                    name="publicityPhoto"
                                    extra={
                                        <>
                                            专家在"我的项目"上传个人宣传照，用于海报、封面图制作等场景
                                            <br />
                                            请上传个人高清照片（半身），建议正装；支持JPG/PNG格式，大小不超过50MB
                                        </>
                                    }
                                >
                                    <Upload customRequest={publicUpload} listType="picture-card" fileList={publicityPhotoList} onRemove={onPublicRemove} onPreview={handlePublicPreview} beforeUpload={publicBeforeUpload}>
                                        {!publicityPhotoList.length && (
                                            <div>
                                                <i className="bi bi-plus"></i>
                                                <div style={{ marginTop: 8 }}>上传宣传照片</div>
                                            </div>
                                        )}
                                    </Upload>

                                    <Modal width={650} open={publicPreviewOpen} footer={null} onCancel={publicPreviewCancel} className="public-preview-modal">
                                        <img alt="example" src={previewImage} className="public-image" />
                                    </Modal>
                                </Form.Item>

                                {/*<Form.Item name="skilledFields" label="擅长领域">*/}
                                {/*    <TextArea*/}
                                {/*        placeholder="请输入领域，多领域用；分开"*/}
                                {/*        className="fill-item"*/}
                                {/*        autoSize={{ minRows: 1 }}*/}
                                {/*    />*/}
                                {/*</Form.Item>*/}

                                {/*<Form.Item name="specialization" label="个人专业">*/}
                                {/*    <TextArea*/}
                                {/*        placeholder="请输入专业，多专业用；分开"*/}
                                {/*        className="fill-item"*/}
                                {/*        autoSize={{ minRows: 1 }}*/}
                                {/*    />*/}
                                {/*</Form.Item>*/}

                                <div id="section-pro" className="form-subtitle">
                                    专业信息
                                </div>

                                <Form.Item name="academicPosition" label="学术任职" extra="如学协会主委、秘书长、杂志评审等，多条用分号（；）隔开">
                                    <TextArea placeholder="请输入学术任职" autoSize={{ minRows: 1, maxRows: 5 }} maxLength={1000} showCount />
                                </Form.Item>

                                <Form.Item name="researchAchievements" label="研究成果" extra="如著作、获奖、主持课题等，多条用分号（；）隔开">
                                    <TextArea placeholder="请输入研究成果" autoSize={{ minRows: 1, maxRows: 5 }} maxLength={2000} showCount />
                                </Form.Item>

                                <Form.Item name="managementExpertises" label="管理专长" extra="擅长医疗管理领域，多条用分号（；）隔开">
                                    <TextArea placeholder="请输入管理专长" autoSize={{ minRows: 1, maxRows: 5 }} maxLength={2000} showCount />
                                </Form.Item>

                                <Form.Item name="medicalExpertises" label="医学专长" extra="擅长医学学术领域，多条用分号（；）隔开">
                                    <TextArea placeholder="请输入医学专长" autoSize={{ minRows: 1, maxRows: 5 }} maxLength={2000} showCount />
                                </Form.Item>

                                <Form.Item name="honoraryTitles" label="荣誉头衔">
                                    {/* <TextArea placeholder="请输入荣誉头衔" autoSize={{ minRows: 1, maxRows: 5 }} maxLength={2000} showCount /> */}
                                    <Select placeholder="请选择荣誉头衔，可多选" mode="multiple" allowClear>
                                        {honoraryList.map((item, index) => (
                                            <Select.Option key={index} value={item}>
                                                {item}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <div id="section-payment" className="form-subtitle form-subtitle--with-trace">
                                    <span className="form-subtitle__text">收款信息</span>
                                    {laborfeeUpdateTrace && (
                                        <span className="expert-phone-update-trace expert-laborfee-update-trace">
                                            <i className="bi bi-clock" />
                                            <span>
                                                最后更新：{laborfeeUpdateTrace.updatedAt}
                                                {laborfeeUpdateTrace.updatedByLabel ? ` ${laborfeeUpdateTrace.updatedByLabel}` : ''}
                                            </span>
                                        </span>
                                    )}
                                </div>

                                <Form.Item name="idcardType" label="证件类型">
                                    <Select
                                        placeholder="请选择证件类型"
                                        onChange={value => {
                                            if (isCnIdCardType(value)) {
                                                const idCardValue = expertForm.getFieldValue('idCard');
                                                const normalized = normalizeIdCardInput(idCardValue, 1);
                                                if (normalized !== idCardValue) {
                                                    expertForm.setFieldValue('idCard', normalized);
                                                }
                                                if (normalized) {
                                                    const birthDate = extractBirthdayFromIdCard(normalized, value);
                                                    if (birthDate) {
                                                        expertForm.setFieldValue('birthday', birthDate);
                                                    }
                                                }
                                                expertForm.setFieldsValue({ nationality: undefined, birthPlace: undefined });
                                            }
                                        }}
                                    >
                                        {cardTypes.map((item, index) => (
                                            <Select.Option key={index} value={item.code}>
                                                {item.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    name="idCard"
                                    className="daoyou-item"
                                    label="证件号"
                                    dependencies={['idcardType']}
                                    normalize={value => normalizeIdCardInput(value, expertForm.getFieldValue('idcardType'))}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (!value) {
                                                    return Promise.resolve();
                                                }
                                                if (isCnIdCardType(idcardType)) {
                                                    if (!/^\d{17}[\dX]$/.test(value)) {
                                                        return Promise.reject(new Error('请输入18位身份证号码，末位可为X'));
                                                    }
                                                    if (!isValidCnIdCard18Checksum(value)) {
                                                        return Promise.reject(new Error('身份证号码校验位不正确（末位需与国家标准算法一致），请核对后重试'));
                                                    }
                                                } else if (idcardType != null && idcardType !== '') {
                                                    // 护照、通行证等常见：字母数字、连字符、点、斜杠、括号等（空格已在 normalize 中去除）
                                                    if (!/^[A-Za-z0-9.\-()/]{5,40}$/.test(value)) {
                                                        return Promise.reject(new Error('证件号格式不正确（5～40位，英文、数字及常用符号）'));
                                                    }
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Input placeholder="请输入证件号" />
                                </Form.Item>

                                <Form.Item label="出生日期" name="birthday" extra={isCnIdCardType(idcardType) ? '证件类型为身份证时，根据证件号自动识别，无需手动填写' : undefined}>
                                    <DatePicker placement="bottomLeft" style={{ width: '100%' }} disabled={isCnIdCardType(idcardType)} />
                                </Form.Item>

                                {!isCnIdCardType(idcardType) && (
                                    <>
                                        <Form.Item name="nationality" label="国籍">
                                            <Select fieldNames={{ label: 'dicName', value: 'dicName' }} options={nationalityDicList} placeholder="请选择国籍" showSearch={{ filterOption: (input, option) => (option?.dicName ?? '').includes(input) }} />
                                        </Form.Item>

                                        <Form.Item name="birthPlace" label="出生城市">
                                            <Input placeholder="请输入出生城市" />
                                        </Form.Item>
                                    </>
                                )}

                                <Form.Item label="银行账号类型" name="bankType" initialValue={1}>
                                    <Radio.Group onChange={e => setBankAccountType(e.target.value)}>
                                        <Radio value={1}>境内银行账户</Radio>
                                        <Radio value={2}>境外银行账户</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                {bankAccountType === 1 && (
                                    <>
                                        <Form.Item label="开户银行" name="bankName">
                                            <Input placeholder="请输入开户行名称" />
                                        </Form.Item>

                                        <Form.Item label="银行账号" name="bankCardNo">
                                            <Input
                                                placeholder="请输入银行账号"
                                                onChange={e => {
                                                    const cleaned = digitsOnlyBankAccount(e.target.value);
                                                    if (cleaned !== e.target.value) {
                                                        expertForm.setFieldValue('bankCardNo', cleaned);
                                                        expertForm.validateFields(['bankCardNo']);
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                    </>
                                )}

                                {bankAccountType === 2 && (
                                    <>
                                        <Form.Item
                                            label="收款人名称（英文）"
                                            name="payeeUsername"
                                            rules={[
                                                { required: true, message: '请输入收款人名称（英文）' },
                                                {
                                                    // 英文姓名常见：字母数字、空格，以及 . , & ' - ( )（缩写、撇号、连字名、Smith, John、括号注记等）
                                                    pattern: /^[A-Za-z0-9\s.,&'()-]+$/,
                                                    message: '请使用英文及常见姓名符号',
                                                },
                                            ]}
                                        >
                                            <Input placeholder="请输入收款人名称（英文）" />
                                        </Form.Item>

                                        <Form.Item label="收款人银行预留电话" name="payeePhone">
                                            <Input placeholder="请输入收款人银行预留电话" />
                                        </Form.Item>

                                        <Form.Item
                                            label="收款人境外汇款住址（英文）"
                                            name="payeeAddress"
                                            rules={[
                                                { required: true, message: '请输入收款人境外汇款住址（英文）' },
                                                {
                                                    // 英文地址常见：字母数字、换行/空格（多行 TextArea），以及 . , # & ' - ( ) /（门牌、单元号、缩写、连字路等）
                                                    pattern: /^[A-Za-z0-9\s.,#&'()/-]+$/,
                                                    message: '请使用英文与常用地址符号',
                                                },
                                            ]}
                                        >
                                            <Input.TextArea placeholder="请输入收款人境外汇款住址（英文）" rows={2} />
                                        </Form.Item>

                                        <Form.Item
                                            label="开户银行名称（英文）"
                                            name="bankName"
                                            rules={[
                                                { required: true, message: '请输入开户银行名称（英文）' },
                                                {
                                                    // 英文行名常见：字母数字、空格，以及 . , & ' - ( ) / 等（公司名、连字符、括号等）
                                                    pattern: /^[A-Za-z0-9\s.,&'()/-]+$/,
                                                    message: '请使用英文及常见银行名称符号',
                                                },
                                            ]}
                                        >
                                            <Input placeholder="请输入开户银行名称（英文）" />
                                        </Form.Item>

                                        <Form.Item label="银行账号" name="bankCardNo">
                                            <Input
                                                placeholder="请输入银行账号"
                                                onChange={e => {
                                                    const cleaned = digitsOnlyBankAccount(e.target.value);
                                                    if (cleaned !== e.target.value) {
                                                        expertForm.setFieldValue('bankCardNo', cleaned);
                                                        expertForm.validateFields(['bankCardNo']);
                                                    }
                                                }}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="SWIFT/BIC Code"
                                            name="swiftBicCode"
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        if (!value || !String(value).trim()) {
                                                            return Promise.resolve();
                                                        }
                                                        if (!/^[A-Za-z0-9]{8,11}$/.test(String(value).trim())) {
                                                            return Promise.reject(new Error('SWIFT/BIC 为 8 或 11 位字母数字'));
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                        >
                                            <Input placeholder="请输入SWIFT/BIC Code" />
                                        </Form.Item>
                                    </>
                                )}

                                <div id="section-more" className="form-subtitle">
                                    更多信息
                                </div>

                                <Form.Item name="expertWechat" label="微信号">
                                    <Input placeholder="请输入微信号" />
                                </Form.Item>

                                <Form.Item name="expertEmail" className="daoyou-item" label="邮箱">
                                    <Input placeholder="请输入邮箱" />
                                </Form.Item>

                                <Form.Item name="area" label="所在地">
                                    <Cascader options={regionOptions} changeOnSelect placeholder="请选择所在地" />
                                </Form.Item>

                                <Form.Item label="民族" name="national">
                                    <Select placeholder="请选择民族" showSearch={{ filterOption: (input, option) => (option?.label ?? '').includes(input) }} options={national} />
                                </Form.Item>

                                <Form.Item label="毕业院校" name="school">
                                    <Input placeholder="请输入毕业院校" />
                                </Form.Item>

                                <Form.Item label="最高学历" name="education">
                                    <Input placeholder="请输入最高学历" />
                                </Form.Item>

                                <Form.Item name="workingYears" label="工作年限">
                                    <Input placeholder="请输入工作年限" />
                                </Form.Item>

                                {/*<Form.Item name="professional" label="带教专业">*/}
                                {/*    <Input placeholder="请输入带教专业" />*/}
                                {/*</Form.Item>*/}

                                <Form.Item name="eatingHabits" label="饮食习惯">
                                    <Input placeholder="请输入饮食习惯" />
                                </Form.Item>

                                <Form.Item name="hobby" label="爱好">
                                    <Input placeholder="请输入爱好" />
                                </Form.Item>

                                {/*<Form.Item name="socialPosition" label="社会任职">*/}
                                {/*    <Input placeholder="请输入社会任职" />*/}
                                {/*</Form.Item>*/}

                                <Form.Item label="相关联系人">
                                    <Form.List name="contacts">
                                        {(fields, { add, remove }, { errors }) => (
                                            <>
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() => add()}
                                                    disabled={fields.length >= 5}
                                                    icon={
                                                        <span className="anticon">
                                                            <PlusCircle />
                                                        </span>
                                                    }
                                                    style={{ padding: 4 }}
                                                >
                                                    添加联系人
                                                </Button>

                                                {fields.map(({ key, name, ...restField }, index) => (
                                                    <div className="contact-container" key={key}>
                                                        <Row gutter={10}>
                                                            <Col span={8}>
                                                                <Form.Item className="section" {...restField} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} name={[name, 'name']} label="姓名" rules={[{ required: true, message: '姓名不能为空' }]}>
                                                                    <Input placeholder="请输入姓名" />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={8}>
                                                                <Form.Item
                                                                    className="section"
                                                                    {...restField}
                                                                    labelCol={{ span: 24 }}
                                                                    wrapperCol={{ span: 24 }}
                                                                    name={[name, 'phone']}
                                                                    label="手机"
                                                                    rules={[
                                                                        { required: true, message: '手机不能为空' },
                                                                        { pattern: /^1\d{10}$/, message: '手机号码格式不正确' },
                                                                    ]}
                                                                >
                                                                    <Input placeholder="请输入电话" />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={8}>
                                                                <Form.Item className="section" {...restField} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} name={[name, 'email']} label="邮箱">
                                                                    <Input placeholder="请输入邮箱" />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>

                                                        <div className="actions">
                                                            {expertId && expertForm.getFieldsValue()?.contacts?.[index]?.id ? (
                                                                <Popconfirm
                                                                    title="确定删除这个联系人么？"
                                                                    onConfirm={() => {
                                                                        const id = expertForm.getFieldsValue().contacts[index].id;
                                                                        delPeopleApi({ id }).then(res => {
                                                                            if (res.status_code === 200) {
                                                                                remove(name);
                                                                                message.success('删除联系人成功');
                                                                            }
                                                                        });
                                                                    }}
                                                                    okText="确定"
                                                                    cancelText="取消"
                                                                >
                                                                    <Button
                                                                        type="link"
                                                                        className="dark"
                                                                        icon={
                                                                            <span className="anticon">
                                                                                <XCircleFill />
                                                                            </span>
                                                                        }
                                                                    />
                                                                </Popconfirm>
                                                            ) : (
                                                                <Button
                                                                    type="link"
                                                                    className="dark"
                                                                    icon={
                                                                        <span className="anticon">
                                                                            <XCircleFill />
                                                                        </span>
                                                                    }
                                                                    onClick={() => remove(name)}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </Form.List>
                                </Form.Item>

                                <Form.Item name="remarks" label="备注">
                                    <TextArea placeholder="请输入备注" className="fill-item" autoSize={{ minRows: 6, maxRows: 6 }} />
                                </Form.Item>
                            </Form>
                        </div>
                    </Col>
                </Row>
            </div>

            <Modal title="更换手机号" open={visibleChangeExpertMobile} onOk={onChangeExpertMobile} onCancel={closeChangeExpertMobileModal} centered>
                <Form form={changeExpertMobileForm} layout="vertical">
                    <Form.Item
                        name="newMobile"
                        label="新手机号"
                        rules={[
                            { required: true, message: '请输入手机号' },
                            { pattern: /^1(?![01])\d{10}$/, message: '手机号格式错误' },
                        ]}
                    >
                        <Input placeholder="请输入手机号" maxLength={11} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="新增单位"
                className={'createUnitModal'}
                open={visibleCreateUnitModal}
                centered
                onCancel={onCancelCreateUnit}
                footer={[
                    <Button key="ok" type="primary" block size="large" onClick={onCreateUnit}>
                        确定添加
                    </Button>,
                ]}
            >
                <Form form={createUnitForm} size="large">
                    <Form.Item label="单位类型" name="orgType" rules={[{ required: true, message: '未选择单位类型' }]}>
                        <Select placeholder="请选择单位类型" options={orgTypeList.filter(item => !!item.value)} allowClear />
                    </Form.Item>
                    <Form.Item label="单位名称" name="unitName" rules={[{ required: true, message: '未填写所在单位名称' }]} extra="请与组织机构代码证/营业执照名称一致">
                        <Input placeholder="请输入您所在单位全称" maxLength={100} allowClear />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title="预览对外展示信息" open={viewExpertVisible} centered onCancel={() => setViewExpertVisible(false)} footer={null} className={'expertPreviewModal'}>
                <div className="expert-preview">
                    <div className="expert-avatar">
                        {!!fileList && !!fileList.length && fileList[0].url ? (
                            <Avatar src={fileList[0].url} style={{ verticalAlign: 'middle' }} />
                        ) : (
                            <Avatar
                                icon={
                                    <span className="iconUser">
                                        <i className="bi bi-person-fill"></i>
                                    </span>
                                }
                            />
                        )}
                    </div>
                    <div className="expert-info">
                        <h4>{expertForm.getFieldsValue()?.memUsername}</h4>

                        {/* 院士头衔（按 CAS -> CAE 顺序展示，使用通用解析） */}
                        {/* {(() => {
                            const titles = getAcademicianTitles(expertForm.getFieldsValue()?.honoraryTitles);
                            return titles.map((t, i) => <p key={`academician-${i}`}>{t}</p>);
                        })()} */}

                        {(() => {
                            const dictMaps = buildAuthDictMaps(authDict);
                            return (watchedExperiences || []).map(experience => {
                                const childDisplays = (experience.childs || []).map(child => formatOfficeAndPost(child, dictMaps)).filter(Boolean);
                                const displayPostName = childDisplays.join('、');
                                return (
                                    <p key={experience.id || Math.random()}>
                                        {experience.unitName}
                                        {displayPostName}
                                    </p>
                                );
                            });
                        })()}
                    </div>
                </div>
            </Modal>
        </>
    );
});

export default Expert;
